import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'
import CustomModal from '../shared/CustomModal/CustomModal'
import ModalActionsFooter from '../shared/ModalActionsFooter/ModalActionsFooter'
import { PrivacyIntakeProgress } from './PrivacyIntakeProgress'
import {
  ACKNOWLEDGE_PRIVACY_CASE,
  ACK_COPY,
  ACTA_FLOW_COPY,
  ACTA_OPERATION_LABELS,
  ACTA_OPERATION_OPTIONS,
  ACTA_STATUS_LABELS,
  ACTA_VERIFICATION_CHECKS,
  ADD_PRIVACY_RESIDUAL,
  buildResponseAuditJson,
  CASE_STATUS_LABELS,
  CASE_TYPE_LABELS,
  actaDisplayName,
  actaFlowInstructions,
  formatActaAuditPreview,
  formatCloseCasePreview,
  caseFlowStageStatus,
  caseFlowStages,
  caseNeedsPlan,
  caseStageInstructions,
  caseStatusLabel,
  caseTypeSelectLabel,
  CHANNEL_LABELS,
  CLEAR_PRIVACY_RESIDUAL,
  CLOSE_PRIVACY_CASE,
  CLOSE_PRIVACY_ERASURE_ACTA,
  CONSERVATION_HOLD_COPY,
  CREATE_PRIVACY_CASE,
  DECIDE_PRIVACY_CASE_HOLD,
  DENY_PRIVACY_CASE,
  DENIAL_REASON_COPY,
  DISPATCH_PRIVACY_AUDIT_OUTBOX,
  RUN_PRIVACY_EMAIL_INTAKE,
  SET_PRIVACY_EMAIL_INTAKE_LISTENING,
  ERASURE_CASE_TYPES,
  BLOCK_CASE_TYPES,
  BLOCK_PLAYBOOK_STEP,
  blockCasePlaybook,
  PORTABILITY_CASE_TYPES,
  PORTABILITY_PLAYBOOK_STEP,
  portabilityCasePlaybook,
  RECTIFICATION_CASE_TYPES,
  emptyRectificationEvidenceForm,
  rectificationEvidenceFormFromJson,
  rectificationEvidenceJsonFromForm,
  parseRectificationEvidenceJsonClient,
  erasureStepReached,
  erasureWizardState,
  EXTEND_COPY,
  EXTEND_PRIVACY_CASE,
  GENERATE_PRIVACY_ERASURE_ACTA,
  GET_PRIVACY_AUDIT_OUTBOX,
  GET_PRIVACY_CASES,
  GET_PRIVACY_DASHBOARD,
  GET_PRIVACY_EMAIL_INTAKE,
  HOLD_STATUS_LABELS,
  IDENTITY_VERIFICATION_COPY,
  ISSUE_PRIVACY_ERASURE_ACTA,
  LIFT_PRIVACY_CASE_HOLD,
  nextCaseAction,
  parseAuditJson,
  credentialsEvidenceFormFromJson,
  credentialsEvidenceJsonFromForm,
  CREDENTIALS_EVIDENCE_COUNT_KEYS,
  emptyCredentialsEvidenceForm,
  offboardingEvidenceFormFromJson,
  offboardingEvidenceJsonFromForm,
  OFFBOARDING_EVIDENCE_MONGO_KEYS,
  OFFBOARDING_OPERATION_OPTIONS,
  emptyOffboardingEvidenceForm,
  resolveErasureOperationType,
  resolveActaOperationTypeFromCase,
  procedureStepHint,
  procedureStepInstructions,
  procedureStepLabel,
  procedureStepPlaybook,
  PROCEDURE_STEP_COPY,
  dpoPasswordPromptCopy,
  privacyActionNeedsPassword,
  requestCopy,
  REVEAL_PRIVACY_ERASURE_PLAYBOOK,
  RESIDUAL_TYPE_LABELS,
  RESOLVE_PRIVACY_CASE,
  SCOPE_LABELS,
  scopeSelectLabel,
  SET_PRIVACY_CONSERVATION_HOLD,
  stageReview,
  TERMINAL_STATUSES,
  TEMPORAL_HOLD_DECIDE_COPY,
  UPDATE_PRIVACY_CASE_REQUEST,
  UPDATE_PRIVACY_ERASURE_ACTA,
  UPDATE_PRIVACY_PROCEDURE_STEP,
  VERIFY_PRIVACY_CASE_IDENTITY,
  dueSeverity,
  formatDateTime,
  formatDate,
  formatDueLabel,
} from '../../graphql/privacy'
import {
  BUSINESS_DAY_RULE,
  ERASURE_EXCEPTIONS,
  PRIVACY_DEADLINES,
  PRIVACY_HOUSE_RULES,
  PRIVACY_RESPONSE_FORM,
} from '../../constants/privacyDuties'
import { encryptAsync } from '../../utils/encrypt/encrypt'
import './PrivacyList.css'

const EMPTY_CREATE_FORM = {
  type: 'ACCESS',
  email: '',
  rut: '',
  name: '',
  companyId: '',
  userId: '',
  scope: 'COMPANY',
  requestDetail: '',
  requestTarget: '',
  receivedAt: '',
}

const ERASURE_TYPES = ERASURE_CASE_TYPES

const OTHER_CHOICE = '__other__'

function FieldLabel({ children, required }) {
  return (
    <span>
      {children}
      {required ? (
        <span className="privacy-required" aria-hidden="true">
          {' '}
          *
        </span>
      ) : null}
    </span>
  )
}

function ChoiceField({
  label,
  hint,
  options,
  value,
  onChange,
  disabled,
  required,
  allowOther = true,
  emptyLabel = 'Selecciona una opción',
}) {
  const [otherMode, setOtherMode] = useState(() => Boolean(value) && !options.includes(value))
  const selectValue = otherMode ? OTHER_CHOICE : value || ''

  return (
    <label className="privacy-field">
      {label ? <FieldLabel required={required}>{label}</FieldLabel> : null}
      {hint ? <small className="privacy-field-hint">{hint}</small> : null}
      <select
        value={selectValue}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value
          if (next === OTHER_CHOICE) {
            setOtherMode(true)
            if (!value || options.includes(value)) onChange('')
            return
          }
          setOtherMode(false)
          onChange(next)
        }}
      >
        <option value="">{emptyLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        {allowOther ? <option value={OTHER_CHOICE}>Otro (especificar)</option> : null}
      </select>
      {otherMode && allowOther ? (
        <input
          placeholder="Describe el caso"
          value={options.includes(value) ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      ) : null}
    </label>
  )
}

function ShieldIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

function BookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  )
}

function InboxIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function ChevronIcon() {
  return (
    <svg
      className="privacy-accordion-chevron"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

/**
 * Plazos en acordeón: la fila cerrada dice cuánto tiempo hay, y al abrirla
 * aparece el procedimiento. Uno abierto a la vez, para que la chuleta no crezca.
 */
function DeadlinesAccordion() {
  const [open, setOpen] = useState(null)

  return (
    <div className="privacy-accordion">
      {PRIVACY_DEADLINES.map((row) => {
        const isOpen = open === row.what
        const panelId = `plazo-${row.what.replace(/\s+/g, '-').toLowerCase()}`
        return (
          <div key={row.what} className={`privacy-accordion-item ${isOpen ? 'open' : ''}`}>
            <button
              type="button"
              className="privacy-accordion-head"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpen(isOpen ? null : row.what)}
            >
              <span className="privacy-accordion-title">
                <ChevronIcon />
                <span>
                  <span className="privacy-guide-label">{row.what}</span>
                  <span className="privacy-guide-detail">{row.note}</span>
                </span>
              </span>
              <span className="privacy-guide-when">{row.when}</span>
              <span className="privacy-guide-cite">{row.cite}</span>
            </button>
            {isOpen ? (
              <ul id={panelId} className="privacy-accordion-panel">
                {row.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** Chuleta de deberes y plazos. Referencia rápida, no un manual. */
function DutiesGuide() {
  return (
    <div className="privacy-guide">
      <section className="privacy-guide-section">
        <h4>
          Plazos
          <small>Toca una fila para ver el procedimiento</small>
        </h4>
        <DeadlinesAccordion />
        <p className="privacy-guide-note">{BUSINESS_DAY_RULE}</p>
      </section>

      <section className="privacy-guide-section">
        <h4>Cómo se responde</h4>
        <ol className="privacy-guide-steps">
          {PRIVACY_RESPONSE_FORM.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      </section>

      <section className="privacy-guide-section">
        <h4>
          Cuándo no procede suprimir
          <small>Art. 7, pág. 7</small>
        </h4>
        <div className="privacy-guide-chips">
          {ERASURE_EXCEPTIONS.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </section>

      <section className="privacy-guide-section">
        <h4>Reglas de la casa</h4>
        <table className="privacy-guide-table">
          <colgroup>
            <col />
            <col className="privacy-guide-col-cite" />
          </colgroup>
          <tbody>
            {PRIVACY_HOUSE_RULES.map((row) => (
              <tr key={row.rule}>
                <td>
                  <span className="privacy-guide-label">{row.rule}</span>
                  <span className="privacy-guide-detail">{row.detail}</span>
                </td>
                <td className="privacy-guide-cite">{row.cite}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  )
}

function DueBadge({ privacyCase }) {
  const severity = dueSeverity(privacyCase)
  if (severity === 'neutral') {
    return <span className="privacy-due neutral">{formatDate(privacyCase.effectiveDueAt)}</span>
  }
  return (
    <span className={`privacy-due ${severity}`}>
      <span className="privacy-due-dot" />
      <span>
        {formatDate(privacyCase.effectiveDueAt)}
        <small>{formatDueLabel(privacyCase)}</small>
      </span>
    </span>
  )
}

function HoldBadge({ hold }) {
  if (!hold?.requestedAt) return <span className="privacy-muted">—</span>
  const status = hold.status || 'NONE'
  const pending = status === 'NONE'
  return (
    <span className={`privacy-hold ${pending ? 'pending' : status.toLowerCase()}`}>
      {HOLD_STATUS_LABELS[status] || status}
      {pending && hold.dueAt ? <small>{formatDate(hold.dueAt)}</small> : null}
    </span>
  )
}

function StepInstructions({ items, title = 'Instructivo' }) {
  if (!items?.length) return null
  return (
    <div className="privacy-instructivo">
      <p className="privacy-instructivo-title">{title}</p>
      <ol className="privacy-step-instructions">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ol>
    </div>
  )
}

function StepPlaybook({ playbook, revealedCommands, onReveal, busy }) {
  const [openCmd, setOpenCmd] = useState(0)
  if (!playbook) return null
  const commands = revealedCommands?.length ? revealedCommands : playbook.commands || []
  const locked = Boolean(playbook.commandsLocked) && !commands.length
  return (
    <div className="privacy-playbook">
      <div className="privacy-playbook-meta">
        <div>
          <span className="privacy-playbook-k">Dónde</span>
          <strong>{playbook.where}</strong>
        </div>
        <div>
          <span className="privacy-playbook-k">Quién</span>
          <strong>{playbook.who}</strong>
        </div>
        {playbook.needsDpoPassword ? (
          <div>
            <span className="privacy-playbook-k">Aprobar</span>
            <strong>{playbook.approveHint || 'Contraseña DPO al marcar Hecho'}</strong>
          </div>
        ) : null}
      </div>
      {playbook.env ? <p className="privacy-playbook-env">{playbook.env}</p> : null}
      {locked ? (
        <div className="privacy-playbook-locked">
          <p className="privacy-playbook-env">
            Los comandos mongosh/AWS de este paso son sensibles. Solo el DPO puede
            verlos tras confirmar con su contraseña del portal.
          </p>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={busy}
            onClick={onReveal}
          >
            Revelar comandos (DPO)
          </button>
        </div>
      ) : null}
      {commands.length ? (
        <div className="privacy-playbook-cmds">
          <p className="privacy-instructivo-title">Comandos</p>
          {commands.map((cmd, index) => {
            const open = openCmd === index
            return (
              <div key={cmd.title} className="privacy-playbook-cmd">
                <button
                  type="button"
                  className="privacy-playbook-cmd-toggle"
                  onClick={() => setOpenCmd(open ? -1 : index)}
                >
                  <span>{cmd.title}</span>
                  <span aria-hidden="true">{open ? '−' : '+'}</span>
                </button>
                {open ? (
                  <pre className="privacy-playbook-code">
                    <code>{cmd.body}</code>
                  </pre>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

function StepFooter({
  onCancel,
  cancelLabel = 'Cancelar',
  confirmLabel,
  onConfirm,
  confirmDisabled,
  busy,
  left,
}) {
  return (
    <div className="privacy-step-footer">
      <ModalActionsFooter
        left={left}
        cancelLabel={cancelLabel}
        onCancel={onCancel}
        confirmLabel={confirmLabel}
        onConfirm={onConfirm}
        confirmDisabled={Boolean(confirmDisabled) || busy}
        confirmLoading={busy}
      />
    </div>
  )
}

/** Resumen + JSON canónico del acta antes de emitir/cerrar. */
function ActaPreview({ acta, privacyCase, residuals }) {
  const openResiduals = (residuals || []).filter((r) => !r.clearedAt)
  const allResiduals = residuals || acta?.residuals || []
  const checks = acta?.verification?.checks || []
  const totals = acta?.totals || {}
  const previewJson = formatActaAuditPreview(acta, privacyCase)
  const totalRows = [
    ['deleted', totals.deleted],
    ['anonymized', totals.anonymized],
    ['s3Deleted', totals.s3Deleted],
    ['s3Tagged', totals.s3Tagged],
    ['credentialsRevoked', totals.credentialsRevoked],
    ['collectionsProcessed', totals.collectionsProcessed],
  ].filter(([, value]) => value != null && Number(value) !== 0)

  return (
    <div className="privacy-acta-preview">
      <div className="privacy-acta-preview-summary">
        <p className="privacy-instructivo-title">Preview del acta</p>
        <ul className="privacy-example-list">
          <li>
            <strong>{actaDisplayName(acta, privacyCase) || acta?.actaNumber || 'Sin número'}</strong>
            {acta?.status ? (
              <span className="privacy-muted">
                {' '}
                · {ACTA_STATUS_LABELS[acta.status] || acta.status}
              </span>
            ) : null}
          </li>
          <li>
            Modo:{' '}
            {ACTA_OPERATION_LABELS[acta?.operationType] || acta?.operationType || '—'}
          </li>
          {privacyCase?.scope ? (
            <li>Alcance: {SCOPE_LABELS[privacyCase.scope] || privacyCase.scope}</li>
          ) : null}
          {acta?.environment ? <li>Entorno: {acta.environment}</li> : null}
          {acta?.signedBy?.dpoEmail || acta?.signedBy?.serverEmail ? (
            <li>
              Firmas:{' '}
              {[
                acta.signedBy.dpoEmail ? `DPO ${acta.signedBy.dpoEmail}` : null,
                acta.signedBy.serverEmail ? `Server ${acta.signedBy.serverEmail}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              {acta.signedBy.signedAt
                ? ` · ${formatDateTime(acta.signedBy.signedAt)}`
                : ''}
            </li>
          ) : null}
          {acta?.issuedAt ? <li>Emitida: {formatDateTime(acta.issuedAt)}</li> : null}
          {acta?.closedAt ? <li>Cerrada: {formatDateTime(acta.closedAt)}</li> : null}
        </ul>

        {totalRows.length ? (
          <>
            <p className="privacy-instructivo-title">Totales</p>
            <ul className="privacy-example-list">
              {totalRows.map(([key, value]) => (
                <li key={key}>
                  {key}: {value}
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {checks.length ? (
          <>
            <p className="privacy-instructivo-title">Comprobaciones</p>
            <ul className="privacy-example-list">
              {checks.map((item) => {
                const meta = ACTA_VERIFICATION_CHECKS.find((c) => c.key === item.key)
                let prettyPayload = ''
                if (item.payloadJson) {
                  try {
                    prettyPayload = JSON.stringify(JSON.parse(item.payloadJson), null, 2)
                  } catch {
                    prettyPayload = item.payloadJson
                  }
                }
                return (
                  <li key={item.key}>
                    <strong>{meta?.label || item.key}</strong>
                    {item.observation ? ` — ${item.observation}` : ''}
                    {prettyPayload ? (
                      <pre className="privacy-acta-preview-json privacy-acta-preview-json--compact">
                        {prettyPayload}
                      </pre>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </>
        ) : null}

        {openResiduals.length ? (
          <>
            <p className="privacy-instructivo-title">Residuales abiertos (quedan en el acta)</p>
            <ul className="privacy-example-list">
              {openResiduals.map((r) => (
                <li key={r.type}>
                  {RESIDUAL_TYPE_LABELS[r.type] || r.type}
                  {r.expiresAt ? ` · vence ${formatDate(r.expiresAt)}` : ''}
                  {r.note ? ` — ${r.note}` : ''}
                </li>
              ))}
            </ul>
          </>
        ) : allResiduals.length ? (
          <>
            <p className="privacy-instructivo-title">Residuales</p>
            <ul className="privacy-example-list">
              {allResiduals.map((r) => (
                <li key={`${r.type}-${r.clearedAt || 'open'}`}>
                  {RESIDUAL_TYPE_LABELS[r.type] || r.type}
                  {r.clearedAt ? ' (cerrado)' : ''}
                  {r.expiresAt ? ` · vence ${formatDate(r.expiresAt)}` : ''}
                  {r.note ? ` — ${r.note}` : ''}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="privacy-muted">Sin residuales abiertos.</p>
        )}
      </div>

      <label className="privacy-field">
        <FieldLabel>JSON completo del acta</FieldLabel>
        <small className="privacy-field-hint">
          Documento canónico que queda en el expediente y cuyo hash puede ir a la bitácora WORM.
        </small>
        <pre className="privacy-acta-preview-json" tabIndex={0}>
          {previewJson || '—'}
        </pre>
      </label>
    </div>
  )
}

/** Checklist P1–P7, hold de conservación, residuals y acta. Un paso visible a la vez. */
function ErasurePanel({ privacyCase, onAction, busy, onClose, footerHost }) {
  const [holdReason, setHoldReason] = useState('')
  const [residualType, setResidualType] = useState('ATLAS_BACKUP')
  const [residualNote, setResidualNote] = useState('')
  const [serverSignerEmail, setServerSignerEmail] = useState('')
  const [checkAuditJson, setCheckAuditJson] = useState('')
  const [credentialsEvidenceJson, setCredentialsEvidenceJson] = useState('')
  const [credentialsEvidenceForm, setCredentialsEvidenceForm] = useState(
    emptyCredentialsEvidenceForm,
  )
  const [credentialsFileName, setCredentialsFileName] = useState('')
  const [showCredentialsJson, setShowCredentialsJson] = useState(false)
  const [offboardingEvidenceJson, setOffboardingEvidenceJson] = useState('')
  const [offboardingEvidenceForm, setOffboardingEvidenceForm] = useState(() =>
    emptyOffboardingEvidenceForm(),
  )
  const [offboardingFileName, setOffboardingFileName] = useState('')
  const [showOffboardingJson, setShowOffboardingJson] = useState(false)
  const [actaOperationType, setActaOperationType] = useState('MIXED')
  const [revealedCommandsByStep, setRevealedCommandsByStep] = useState({})
  const [focusedIndex, setFocusedIndex] = useState(null)
  const [scopeChoice, setScopeChoice] = useState(privacyCase.scope || '')

  useEffect(() => {
    if (privacyCase.scope) setScopeChoice(privacyCase.scope)
  }, [privacyCase.scope])

  const live = erasureWizardState(privacyCase)
  const steps = privacyCase.procedure?.steps || []
  const displayIndex =
    focusedIndex != null && erasureStepReached(focusedIndex, privacyCase)
      ? focusedIndex
      : live.index
  const displayStep = steps[displayIndex] || live.step
  const viewingPast = displayIndex !== live.index
  const wizard =
    viewingPast && displayStep?.code !== 'P6_ACTA'
      ? { kind: 'step', step: displayStep, index: displayIndex, total: live.total }
      : live
  const residuals = privacyCase.residuals || []
  const acta = privacyCase.erasureActa
  const conservationActive = Boolean(privacyCase.conservationHold?.active)
  const currentStep = wizard.step || displayStep
  const check = wizard.check
  const instructions = procedureStepInstructions(
    currentStep,
    privacyCase,
    currentStep?.code === 'P5_OFFBOARDING'
      ? offboardingEvidenceForm.operationType
      : undefined,
  )
  const playbook = procedureStepPlaybook(
    currentStep,
    privacyCase,
    currentStep?.code === 'P5_OFFBOARDING'
      ? offboardingEvidenceForm.operationType
      : undefined,
  )
  const actaInstructions = actaFlowInstructions(wizard.kind, check)
  const credentialsCopy =
    currentStep?.code === 'P7_CREDENTIALS'
      ? PROCEDURE_STEP_COPY.P7_CREDENTIALS
      : null
  const offboardingCopy =
    currentStep?.code === 'P5_OFFBOARDING'
      ? PROCEDURE_STEP_COPY.P5_OFFBOARDING
      : null
  const caption =
    wizard.kind === 'acta-check'
      ? `Acta · comprobación ${wizard.checkIndex + 1} de ${wizard.checkTotal}`
      : wizard.kind === 'acta-generate'
        ? `Paso ${wizard.index + 1} de ${wizard.total} · Emitir el acta`
        : wizard.kind === 'acta-sign'
          ? 'Acta · revisar, firmar y cerrar'
          : wizard.kind === 'acta-close'
            ? 'Acta · cerrar (legado)'
            : wizard.kind === 'done'
              ? 'Eliminación registrada'
              : `Paso ${wizard.index + 1} de ${wizard.total} · ${procedureStepLabel(currentStep)}`

  useEffect(() => {
    if (currentStep?.code !== 'P7_CREDENTIALS') return
    const existing = (currentStep.payloadJson || '').trim()
    const example = PROCEDURE_STEP_COPY.P7_CREDENTIALS.jsonExample || ''
    const seed = existing || example
    setCredentialsEvidenceJson(seed)
    setCredentialsEvidenceForm(credentialsEvidenceFormFromJson(seed))
    setCredentialsFileName('')
    setShowCredentialsJson(false)
  }, [currentStep?.code, currentStep?.payloadJson])

  useEffect(() => {
    if (currentStep?.code !== 'P5_OFFBOARDING') return
    const existing = (currentStep.payloadJson || '').trim()
    const op =
      resolveErasureOperationType(privacyCase) ||
      privacyCase?.erasureActa?.operationType ||
      'PHYSICAL'
    const seed =
      existing ||
      offboardingEvidenceJsonFromForm(emptyOffboardingEvidenceForm(op))
    setOffboardingEvidenceJson(seed)
    setOffboardingEvidenceForm(offboardingEvidenceFormFromJson(seed, op))
    setOffboardingFileName('')
    setShowOffboardingJson(false)
  }, [
    currentStep?.code,
    currentStep?.payloadJson,
    privacyCase?.erasureActa?.operationType,
    privacyCase?.request?.plannedAction,
    privacyCase?.scope,
  ])

  useEffect(() => {
    if (wizard.kind !== 'acta-generate') return
    setActaOperationType(resolveActaOperationTypeFromCase(privacyCase))
  }, [wizard.kind, privacyCase?.erasureActa?.operationType, privacyCase?.procedure?.steps, privacyCase?.request?.plannedAction])

  const applyCredentialsForm = (nextForm) => {
    setCredentialsEvidenceForm(nextForm)
    setCredentialsEvidenceJson(credentialsEvidenceJsonFromForm(nextForm))
    setCredentialsFileName('')
  }

  const setCountField = (key, side, raw) => {
    const value = raw === '' ? 0 : Number(raw)
    applyCredentialsForm({
      ...credentialsEvidenceForm,
      counts: {
        ...credentialsEvidenceForm.counts,
        [key]: {
          ...credentialsEvidenceForm.counts[key],
          [side]: Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0,
        },
      },
    })
  }

  const applyOffboardingForm = (nextForm, { resetCommands = false } = {}) => {
    setOffboardingEvidenceForm(nextForm)
    setOffboardingEvidenceJson(offboardingEvidenceJsonFromForm(nextForm))
    setOffboardingFileName('')
    if (resetCommands) {
      setRevealedCommandsByStep((prev) => {
        if (!prev.P5_OFFBOARDING) return prev
        const next = { ...prev }
        delete next.P5_OFFBOARDING
        return next
      })
    }
  }

  const setOffboardingMongoField = (key, side, raw) => {
    const value = raw === '' ? 0 : Number(raw)
    applyOffboardingForm({
      ...offboardingEvidenceForm,
      mongo: {
        ...offboardingEvidenceForm.mongo,
        [key]: {
          ...offboardingEvidenceForm.mongo[key],
          [side]: Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0,
        },
      },
    })
  }

  const setOffboardingS3Field = (bucket, side, raw) => {
    const value = raw === '' ? 0 : Number(raw)
    applyOffboardingForm({
      ...offboardingEvidenceForm,
      s3: {
        ...offboardingEvidenceForm.s3,
        [bucket]: {
          ...offboardingEvidenceForm.s3[bucket],
          [side]: Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0,
        },
      },
    })
  }

  const markStep = async (status, extra = {}) => {
    await onAction('updateStep', {
      input: {
        caseCode: privacyCase.caseCode,
        code: currentStep.code,
        status,
        ...extra,
      },
    })
    setFocusedIndex(null)
  }

  const confirmCurrentStep = async () => {
    if (currentStep?.code === 'P1_INTAKE') {
      if (!scopeChoice) return
      const ok = await onAction('updateRequest', {
        input: { caseCode: privacyCase.caseCode, scope: scopeChoice },
      })
      if (!ok) return
      await markStep('DONE', {
        note: `Alcance: ${SCOPE_LABELS[scopeChoice] || scopeChoice}`,
      })
      return
    }
    if (currentStep?.code === 'P7_CREDENTIALS') {
      if (!parsedCredentialsJson.ok) return
      await markStep('DONE', { payloadJson: parsedCredentialsJson.text })
      return
    }
    if (currentStep?.code === 'P5_OFFBOARDING') {
      if (!parsedOffboardingJson.ok) return
      await markStep('DONE', { payloadJson: parsedOffboardingJson.text })
      return
    }
    await markStep(
      currentStep?.code === 'P3_LEGAL_HOLD' && !conservationActive ? 'SKIPPED' : 'DONE',
    )
  }

  const parsedCheckJson = parseAuditJson(checkAuditJson)
  const parsedCredentialsJson = parseAuditJson(credentialsEvidenceJson)
  const parsedOffboardingJson = parseAuditJson(offboardingEvidenceJson)
  const canAdvanceCheck = parsedCheckJson.ok
  const canAdvanceStepEvidence =
    (currentStep?.code !== 'P7_CREDENTIALS' || parsedCredentialsJson.ok) &&
    (currentStep?.code !== 'P5_OFFBOARDING' || parsedOffboardingJson.ok)

  const loadCredentialsFile = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      setCredentialsEvidenceJson(text)
      setCredentialsEvidenceForm(credentialsEvidenceFormFromJson(text))
      setCredentialsFileName(file.name || '')
    }
    reader.readAsText(file)
  }

  const loadOffboardingFile = (file) => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : ''
      const op =
        resolveErasureOperationType(privacyCase) ||
        privacyCase?.erasureActa?.operationType ||
        'PHYSICAL'
      setOffboardingEvidenceJson(text)
      setOffboardingEvidenceForm(offboardingEvidenceFormFromJson(text, op))
      setOffboardingFileName(file.name || '')
    }
    reader.readAsText(file)
  }

  useEffect(() => {
    if (wizard.kind !== 'acta-check' || !check?.jsonExample) return
    setCheckAuditJson(check.jsonExample)
  }, [wizard.kind, check?.key, check?.jsonExample])

  const advanceCheck = async () => {
    if (!parsedCheckJson.ok) return
    await onAction('updateActa', {
      input: {
        caseCode: privacyCase.caseCode,
        verification: {
          [check.key]: true,
          payloadJson: parsedCheckJson.text,
        },
      },
    })
    setCheckAuditJson('')
  }

  const erasureFooter =
    wizard.kind === 'step' ? (
      <StepFooter
        onCancel={onClose}
        left={
          currentStep &&
          currentStep.status !== 'SKIPPED' &&
          currentStep.code !== 'P1_INTAKE' ? (
            <button
              className="btn btn-outline btn-sm"
              disabled={busy}
              onClick={() => markStep('SKIPPED')}
            >
              Omitir
            </button>
          ) : null
        }
        confirmLabel="Siguiente paso"
        confirmDisabled={
          currentStep?.status === 'BLOCKED' ||
          (currentStep?.code === 'P1_INTAKE' && !scopeChoice) ||
          !canAdvanceStepEvidence
        }
        onConfirm={confirmCurrentStep}
        busy={busy}
      />
    ) : wizard.kind === 'acta-generate' ? (
      <StepFooter
        onCancel={onClose}
        confirmLabel="Crear borrador del acta"
        confirmDisabled={!actaOperationType}
        onConfirm={() =>
          onAction('generateActa', {
            input: {
              caseCode: privacyCase.caseCode,
              operationType: actaOperationType,
            },
          })
        }
        busy={busy}
      />
    ) : wizard.kind === 'acta-check' ? (
      <StepFooter
        onCancel={onClose}
        confirmLabel="Siguiente paso"
        confirmDisabled={!canAdvanceCheck}
        onConfirm={advanceCheck}
        busy={busy}
      />
    ) : wizard.kind === 'acta-sign' ? (
      <StepFooter
        onCancel={onClose}
        confirmLabel="Emitir y cerrar acta"
        confirmDisabled={!serverSignerEmail.trim()}
        onConfirm={() =>
          onAction('issueActa', {
            input: {
              caseCode: privacyCase.caseCode,
              serverSignerEmail: serverSignerEmail.trim(),
            },
          })
        }
        busy={busy}
      />
    ) : wizard.kind === 'acta-close' ? (
      <StepFooter
        onCancel={onClose}
        confirmLabel="Siguiente paso"
        onConfirm={() =>
          onAction('closeActa', { input: { caseCode: privacyCase.caseCode } })
        }
        busy={busy}
      />
    ) : null

  return (
    <section className="privacy-erasure">
      <h4>
        Eliminación de datos
        <small>{SCOPE_LABELS[privacyCase.scope] || 'Sin alcance'}</small>
      </h4>

      <div className="privacy-wizard-rail">
        {steps.map((step, index) => {
          const reached = erasureStepReached(index, privacyCase)
          const statusClass =
            index === live.index && live.kind !== 'done' ? 'current' : step.status.toLowerCase()
          const viewing = index === displayIndex && live.kind !== 'done'
          return (
            <button
              key={step.code}
              type="button"
              className={`privacy-wizard-dot ${statusClass}${viewing ? ' viewing' : ''}`}
              title={procedureStepLabel(step)}
              disabled={!reached}
              onClick={() => setFocusedIndex(index === live.index ? null : index)}
            >
              <span className="privacy-wizard-dot-num">{index + 1}</span>
              {viewing || (index === live.index && live.kind !== 'done') ? (
                <span className="privacy-wizard-dot-label">{procedureStepLabel(step)}</span>
              ) : null}
            </button>
          )
        })}
      </div>
      {wizard.kind !== 'step' && wizard.kind !== 'done' ? (
        <p className="privacy-wizard-caption">{caption}</p>
      ) : null}
      {viewingPast ? (
        <p className="privacy-flow-hint">
          Es posible corregir este paso. Al confirmar se guarda y se vuelve al paso actual del flujo.
        </p>
      ) : null}

      {conservationActive && wizard.kind === 'step' && currentStep?.status === 'BLOCKED' ? (
        <div className="privacy-note warning">
          Hold de conservación activo: este paso no avanza hasta levantarlo.
        </div>
      ) : null}

      {wizard.kind === 'step' && currentStep?.code === 'P1_INTAKE' ? (
        <label className="privacy-field">
          <FieldLabel required>Alcance de la eliminación</FieldLabel>
          <small className="privacy-field-hint">
            Qué se va a borrar o anonimizar. Queda en el expediente y en el acta.
          </small>
          <select
            value={scopeChoice}
            disabled={busy}
            required
            onChange={(e) => setScopeChoice(e.target.value)}
          >
            <option value="">Selecciona una opción</option>
            {Object.keys(SCOPE_LABELS).map((value) => (
              <option key={value} value={value}>
                {scopeSelectLabel(value)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {wizard.kind === 'step' && currentStep?.code === 'P3_LEGAL_HOLD' ? (
        <div className="privacy-inline-form column">
          {conservationActive ? (
            <>
              <span className="privacy-hold active">Hold de conservación activo</span>
              <div className="privacy-actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={busy}
                  onClick={() =>
                    onAction('setConservationHold', {
                      input: { caseCode: privacyCase.caseCode, active: false },
                    })
                  }
                >
                  Levantar hold de conservación
                </button>
              </div>
            </>
          ) : (
            <>
              <ChoiceField
                label={CONSERVATION_HOLD_COPY.fieldLabel}
                hint={CONSERVATION_HOLD_COPY.intro}
                options={CONSERVATION_HOLD_COPY.options}
                value={holdReason}
                onChange={setHoldReason}
                disabled={busy}
              />
              <div className="privacy-actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={busy || !holdReason.trim()}
                  onClick={() =>
                    onAction('setConservationHold', {
                      input: {
                        caseCode: privacyCase.caseCode,
                        active: true,
                        reason: holdReason.trim(),
                      },
                    })
                  }
                >
                  Activar hold y continuar después
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {wizard.kind === 'step' && currentStep?.code === 'P4_BACKUPS' ? (
        <div className="privacy-inline-form column">
          <ul className="privacy-residuals">
            {residuals.length ? (
              residuals.map((item) => (
                <li key={`${item.type}-${item.clearedAt || 'open'}`}>
                  {RESIDUAL_TYPE_LABELS[item.type] || item.type}
                  {item.note ? ` — ${item.note}` : ''}
                  {item.clearedAt ? (
                    <span className="privacy-muted"> (cerrado)</span>
                  ) : (
                    <button
                      className="btn btn-outline btn-sm"
                      disabled={busy}
                      onClick={() =>
                        onAction('clearResidual', {
                          input: { caseCode: privacyCase.caseCode, type: item.type },
                        })
                      }
                    >
                      Cerrar
                    </button>
                  )}
                </li>
              ))
            ) : (
              <li className="privacy-muted">Sin residuales todavía. Si Atlas u Object Lock dejan rastro, regístralo.</li>
            )}
          </ul>
          <div className="privacy-inline-form">
            <select value={residualType} onChange={(e) => setResidualType(e.target.value)}>
              {Object.entries(RESIDUAL_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <input
              placeholder="Nota / vencimiento"
              value={residualNote}
              onChange={(e) => setResidualNote(e.target.value)}
            />
            <button
              className="btn btn-outline btn-sm"
              disabled={busy}
              onClick={() =>
                onAction('addResidual', {
                  input: {
                    caseCode: privacyCase.caseCode,
                    type: residualType,
                    note: residualNote.trim() || undefined,
                  },
                })
              }
            >
              Registrar residual
            </button>
          </div>
        </div>
      ) : null}

      {wizard.kind === 'step' ? (
        <>
          <p className="privacy-step-intro">
            <span className="privacy-step-num">
              Paso {wizard.index + 1} de {wizard.total}
            </span>
            {procedureStepHint(
              currentStep,
              privacyCase,
              currentStep?.code === 'P5_OFFBOARDING'
                ? offboardingEvidenceForm.operationType
                : undefined,
            ) || 'Marca el paso cuando Server o TI lo hayan hecho.'}
          </p>
          {currentStep?.code === 'P5_OFFBOARDING' ? (
            <label className="privacy-field privacy-p5-mode">
              <FieldLabel required>Modo de operación</FieldLabel>
              <small className="privacy-field-hint">
                Define el instructivo y los comandos (borrado, anonimización o mixto).
                Si ya revelaste comandos, al cambiar el modo hay que volver a revelarlos.
              </small>
              <select
                disabled={busy}
                value={offboardingEvidenceForm.operationType || 'PHYSICAL'}
                onChange={(e) =>
                  applyOffboardingForm(
                    {
                      ...offboardingEvidenceForm,
                      operationType: e.target.value,
                    },
                    { resetCommands: true },
                  )
                }
              >
                {OFFBOARDING_OPERATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <StepPlaybook
            key={
              currentStep?.code === 'P5_OFFBOARDING'
                ? `P5-${offboardingEvidenceForm.operationType}`
                : currentStep?.code
            }
            playbook={playbook}
            revealedCommands={
              currentStep?.code ? revealedCommandsByStep[currentStep.code] : null
            }
            busy={busy}
            onReveal={() => {
              if (!currentStep?.code) return
              onAction('revealPlaybook', {
                input: {
                  caseCode: privacyCase.caseCode,
                  stepCode: currentStep.code,
                  ...(currentStep.code === 'P5_OFFBOARDING'
                    ? {
                        operationType:
                          offboardingEvidenceForm.operationType || 'PHYSICAL',
                      }
                    : {}),
                },
                onRevealed: (revealed) => {
                  setRevealedCommandsByStep((prev) => ({
                    ...prev,
                    [currentStep.code]: revealed?.commands || [],
                  }))
                },
              })
            }}
          />
          <StepInstructions items={instructions} />
          {currentStep?.code === 'P7_CREDENTIALS' && credentialsCopy ? (
            <div className="privacy-inline-form column privacy-evidence-builder">
              <div className="privacy-field">
                <FieldLabel required>Evidencia de credenciales</FieldLabel>
                <small className="privacy-field-hint">{credentialsCopy.jsonHint}</small>
                <div className="privacy-file-row">
                  <label className="privacy-file-pick">
                    <span className="btn btn-outline btn-sm">Adjuntar .json</span>
                    <input
                      type="file"
                      accept=".json,application/json"
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        loadCredentialsFile(file)
                        e.target.value = ''
                      }}
                    />
                  </label>
                  {credentialsFileName ? (
                    <span className="privacy-muted">{credentialsFileName}</span>
                  ) : (
                    <span className="privacy-muted">O completa los conteos abajo</span>
                  )}
                </div>

                <p className="privacy-instructivo-title">Conteos (antes → después)</p>
                <div className="privacy-evidence-counts">
                  <div className="privacy-evidence-count-head" aria-hidden="true">
                    <span>Colección</span>
                    <span>Antes</span>
                    <span>Después</span>
                  </div>
                  {CREDENTIALS_EVIDENCE_COUNT_KEYS.map(({ key, label }) => (
                    <div key={key} className="privacy-evidence-count-row">
                      <span className="privacy-evidence-count-label">{label}</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        disabled={busy}
                        value={credentialsEvidenceForm.counts[key]?.before ?? 0}
                        onChange={(e) => setCountField(key, 'before', e.target.value)}
                        aria-label={`${key} antes`}
                      />
                      <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        disabled={busy}
                        value={credentialsEvidenceForm.counts[key]?.after ?? 0}
                        onChange={(e) => setCountField(key, 'after', e.target.value)}
                        aria-label={`${key} después`}
                      />
                    </div>
                  ))}
                </div>

                <p className="privacy-instructivo-title">Prueba de corte</p>
                <div className="privacy-evidence-checks">
                  <label className="privacy-evidence-check">
                    <input
                      type="checkbox"
                      disabled={busy}
                      checked={!credentialsEvidenceForm.cutTest.botAuthenticated}
                      onChange={(e) =>
                        applyCredentialsForm({
                          ...credentialsEvidenceForm,
                          cutTest: {
                            ...credentialsEvidenceForm.cutTest,
                            botAuthenticated: !e.target.checked,
                          },
                        })
                      }
                    />
                    <span>El bot ya no autentica</span>
                  </label>
                  <label className="privacy-evidence-check">
                    <input
                      type="checkbox"
                      disabled={busy}
                      checked={!credentialsEvidenceForm.cutTest.botWrote}
                      onChange={(e) =>
                        applyCredentialsForm({
                          ...credentialsEvidenceForm,
                          cutTest: {
                            ...credentialsEvidenceForm.cutTest,
                            botWrote: !e.target.checked,
                          },
                        })
                      }
                    />
                    <span>El bot ya no escribe datos</span>
                  </label>
                </div>

                <label className="privacy-field">
                  <FieldLabel>Nota (opcional)</FieldLabel>
                  <input
                    type="text"
                    disabled={busy}
                    value={credentialsEvidenceForm.note || ''}
                    onChange={(e) =>
                      applyCredentialsForm({
                        ...credentialsEvidenceForm,
                        note: e.target.value,
                      })
                    }
                    placeholder="Ej. freeze Rabbit aplicado; staging verificado"
                  />
                </label>

                <button
                  type="button"
                  className="btn btn-outline btn-sm privacy-evidence-json-toggle"
                  disabled={busy}
                  onClick={() => setShowCredentialsJson((v) => !v)}
                >
                  {showCredentialsJson ? 'Ocultar JSON' : 'Ver / editar JSON'}
                </button>
                {showCredentialsJson ? (
                  <>
                    <textarea
                      className="privacy-json"
                      spellCheck={false}
                      value={credentialsEvidenceJson}
                      onChange={(e) => {
                        const text = e.target.value
                        setCredentialsEvidenceJson(text)
                        setCredentialsFileName('')
                        const parsed = parseAuditJson(text)
                        if (parsed.ok) {
                          setCredentialsEvidenceForm(
                            credentialsEvidenceFormFromJson(parsed.text),
                          )
                        }
                      }}
                      required
                      disabled={busy}
                    />
                    {credentialsEvidenceJson.trim() && !parsedCredentialsJson.ok ? (
                      <small className="privacy-field-error">
                        {parsedCredentialsJson.error}
                      </small>
                    ) : null}
                  </>
                ) : credentialsEvidenceJson.trim() && !parsedCredentialsJson.ok ? (
                  <small className="privacy-field-error">{parsedCredentialsJson.error}</small>
                ) : null}
              </div>
            </div>
          ) : null}
          {currentStep?.code === 'P5_OFFBOARDING' && offboardingCopy ? (
            <div className="privacy-inline-form column privacy-evidence-builder">
              <div className="privacy-field">
                <FieldLabel required>Evidencia de borrado / anonimización</FieldLabel>
                <small className="privacy-field-hint">{offboardingCopy.jsonHint}</small>
                <div className="privacy-file-row">
                  <label className="privacy-file-pick">
                    <span className="btn btn-outline btn-sm">Adjuntar .json</span>
                    <input
                      type="file"
                      accept=".json,application/json"
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        loadOffboardingFile(file)
                        e.target.value = ''
                      }}
                    />
                  </label>
                  {offboardingFileName ? (
                    <span className="privacy-muted">{offboardingFileName}</span>
                  ) : (
                    <span className="privacy-muted">O completa los conteos abajo</span>
                  )}
                </div>

                <p className="privacy-instructivo-title">Mongo (antes → después)</p>
                <div className="privacy-evidence-counts privacy-evidence-counts-scroll">
                  <div className="privacy-evidence-count-head" aria-hidden="true">
                    <span>Colección</span>
                    <span>Antes</span>
                    <span>Después</span>
                  </div>
                  {OFFBOARDING_EVIDENCE_MONGO_KEYS.map(({ key, label }) => (
                    <div key={key} className="privacy-evidence-count-row">
                      <span className="privacy-evidence-count-label">{label}</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        disabled={busy}
                        value={offboardingEvidenceForm.mongo[key]?.before ?? 0}
                        onChange={(e) =>
                          setOffboardingMongoField(key, 'before', e.target.value)
                        }
                        aria-label={`${key} antes`}
                      />
                      <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        disabled={busy}
                        value={offboardingEvidenceForm.mongo[key]?.after ?? 0}
                        onChange={(e) =>
                          setOffboardingMongoField(key, 'after', e.target.value)
                        }
                        aria-label={`${key} después`}
                      />
                    </div>
                  ))}
                </div>

                <p className="privacy-instructivo-title">
                  S3 (objetos antes → después · bypass GOVERNANCE)
                </p>
                <div className="privacy-evidence-counts">
                  <div className="privacy-evidence-count-head" aria-hidden="true">
                    <span>Prefijo</span>
                    <span>Antes</span>
                    <span>Después</span>
                  </div>
                  <div className="privacy-evidence-count-row">
                    <span className="privacy-evidence-count-label">
                      clients/{'{companyId}'}/
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      disabled={busy}
                      value={offboardingEvidenceForm.s3.clients?.before ?? 0}
                      onChange={(e) =>
                        setOffboardingS3Field('clients', 'before', e.target.value)
                      }
                      aria-label="S3 clients antes"
                    />
                    <input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      disabled={busy}
                      value={offboardingEvidenceForm.s3.clients?.after ?? 0}
                      onChange={(e) =>
                        setOffboardingS3Field('clients', 'after', e.target.value)
                      }
                      aria-label="S3 clients después"
                    />
                  </div>
                  <div className="privacy-evidence-count-row">
                    <span className="privacy-evidence-count-label">
                      intake …/cases/{'{CASO}'}/
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      disabled={busy}
                      value={offboardingEvidenceForm.s3.intake?.before ?? 0}
                      onChange={(e) =>
                        setOffboardingS3Field('intake', 'before', e.target.value)
                      }
                      aria-label="S3 intake antes"
                    />
                    <input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      disabled={busy}
                      value={offboardingEvidenceForm.s3.intake?.after ?? 0}
                      onChange={(e) =>
                        setOffboardingS3Field('intake', 'after', e.target.value)
                      }
                      aria-label="S3 intake después"
                    />
                  </div>
                </div>

                <label className="privacy-field">
                  <FieldLabel required>Hits *_fuzzy después</FieldLabel>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    disabled={busy}
                    value={offboardingEvidenceForm.fuzzyHitsAfter ?? 0}
                    onChange={(e) => {
                      const raw = e.target.value
                      const value = raw === '' ? 0 : Number(raw)
                      applyOffboardingForm({
                        ...offboardingEvidenceForm,
                        fuzzyHitsAfter: Number.isFinite(value)
                          ? Math.max(0, Math.floor(value))
                          : 0,
                      })
                    }}
                  />
                </label>

                <label className="privacy-field">
                  <FieldLabel>Nota (opcional)</FieldLabel>
                  <input
                    type="text"
                    disabled={busy}
                    value={offboardingEvidenceForm.note || ''}
                    onChange={(e) =>
                      applyOffboardingForm({
                        ...offboardingEvidenceForm,
                        note: e.target.value,
                      })
                    }
                    placeholder="Ej. cashpositions fuera de cascada; residual justificado"
                  />
                </label>

                <button
                  type="button"
                  className="btn btn-outline btn-sm privacy-evidence-json-toggle"
                  disabled={busy}
                  onClick={() => setShowOffboardingJson((v) => !v)}
                >
                  {showOffboardingJson ? 'Ocultar JSON' : 'Ver / editar JSON'}
                </button>
                {showOffboardingJson ? (
                  <>
                    <textarea
                      className="privacy-json"
                      spellCheck={false}
                      value={offboardingEvidenceJson}
                      onChange={(e) => {
                        const text = e.target.value
                        setOffboardingEvidenceJson(text)
                        setOffboardingFileName('')
                        const parsed = parseAuditJson(text)
                        if (parsed.ok) {
                          const op =
                            resolveErasureOperationType(privacyCase) ||
                            privacyCase?.erasureActa?.operationType ||
                            'PHYSICAL'
                          setOffboardingEvidenceForm(
                            offboardingEvidenceFormFromJson(parsed.text, op),
                          )
                        }
                      }}
                      required
                      disabled={busy}
                    />
                    {offboardingEvidenceJson.trim() && !parsedOffboardingJson.ok ? (
                      <small className="privacy-field-error">
                        {parsedOffboardingJson.error}
                      </small>
                    ) : null}
                  </>
                ) : offboardingEvidenceJson.trim() && !parsedOffboardingJson.ok ? (
                  <small className="privacy-field-error">{parsedOffboardingJson.error}</small>
                ) : null}
              </div>
            </div>
          ) : null}
          {currentStep?.note ? <p className="privacy-muted">{currentStep.note}</p> : null}
        </>
      ) : null}

      {wizard.kind === 'acta-generate' ? (
        <>
          <p className="privacy-step-intro">
            {actaDisplayName(acta, privacyCase) ||
              'Confirmar el modo de operación y crear el borrador del acta de eliminación.'}
          </p>
          <StepInstructions items={actaInstructions} />

          <label className="privacy-field">
            <FieldLabel required>Modo de operación del acta</FieldLabel>
            <small className="privacy-field-hint">
              Debe coincidir con lo ejecutado en P5 (borrado, anonimización o mixto). Se
              prellena desde el plan o la evidencia del paso P5 cuando existe.
            </small>
            <select
              disabled={busy}
              value={actaOperationType || 'MIXED'}
              onChange={(e) => setActaOperationType(e.target.value)}
            >
              {ACTA_OPERATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          {privacyCase.request?.plannedAction ? (
            <p className="privacy-muted">
              <strong>Plan registrado:</strong> {privacyCase.request.plannedAction}
            </p>
          ) : null}

          {residuals.filter((r) => !r.clearedAt).length ? (
            <div className="privacy-acta-residuals">
              <p className="privacy-instructivo-title">Residuales abiertos (van al acta)</p>
              <ul className="privacy-example-list">
                {residuals
                  .filter((r) => !r.clearedAt)
                  .map((r) => (
                    <li key={r.type}>
                      {RESIDUAL_TYPE_LABELS[r.type] || r.type}
                      {r.expiresAt ? ` · vence ${formatDate(r.expiresAt)}` : ''}
                      {r.note ? ` — ${r.note}` : ''}
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}

          <div className="privacy-acta-checks-preview">
            <p className="privacy-instructivo-title">
              Comprobaciones siguientes (una por una, con JSON)
            </p>
            <ol className="privacy-acta-checks-list">
              {ACTA_VERIFICATION_CHECKS.map((item) => (
                <li key={item.key}>
                  <strong>{item.label}</strong>
                  <span className="privacy-muted"> — {item.hint}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      ) : null}

      {wizard.kind === 'acta-check' ? (
        <>
          <p className="privacy-step-intro">
            <strong>{check.label}.</strong> {check.hint} Completar el JSON de lo comprobado: se
            incorpora al JSON del acta y a la bitácora (10 años). No un enlace.
          </p>
          <StepInstructions items={actaInstructions} />
          <label className="privacy-field">
            <FieldLabel required>JSON de auditoría</FieldLabel>
            <small className="privacy-field-hint">{check.jsonHint}</small>
            <textarea
              className="privacy-json"
              spellCheck={false}
              value={checkAuditJson}
              onChange={(e) => setCheckAuditJson(e.target.value)}
              required
            />
            {checkAuditJson.trim() && !parsedCheckJson.ok ? (
              <small className="privacy-field-error">{parsedCheckJson.error}</small>
            ) : null}
          </label>
        </>
      ) : null}

      {wizard.kind === 'acta-sign' ? (
        <>
          <p className="privacy-step-intro">
            Revisar el preview del acta. Al confirmar, se emite y se cierra en un solo paso
            (firman DPO y Server, personas distintas).
          </p>
          <StepInstructions items={actaInstructions} />
          <ActaPreview acta={acta} privacyCase={privacyCase} residuals={residuals} />
          <label className="privacy-field">
            <FieldLabel required>Correo del firmante Server</FieldLabel>
            <input
              placeholder="distinto al DPO"
              value={serverSignerEmail}
              onChange={(e) => setServerSignerEmail(e.target.value)}
              required
            />
          </label>
        </>
      ) : null}

      {wizard.kind === 'acta-close' ? (
        <>
          <p className="privacy-step-intro">
            Acta {ACTA_STATUS_LABELS[acta?.status] || acta?.status}
            {acta?.signedBy?.dpoEmail ? ` · firmada por ${acta.signedBy.dpoEmail}` : ''}.
            Este paso solo aparece en actas emitidas antes del cierre unificado.
          </p>
          <StepInstructions items={actaInstructions} />
          <ActaPreview acta={acta} privacyCase={privacyCase} residuals={residuals} />
        </>
      ) : null}

      {wizard.kind === 'done' ? (
        <p className="privacy-muted">La eliminación quedó registrada. Sigue la respuesta al titular.</p>
      ) : null}

      {footerHost && erasureFooter ? createPortal(erasureFooter, footerHost) : null}
    </section>
  )
}

function CaseFlow({ privacyCase, viewedStage, onViewStage }) {
  const stages = caseFlowStages(privacyCase)
  return (
    <div className="privacy-flow">
      <ol className="privacy-flow-stages">
        {stages.map((stage) => {
          const status = caseFlowStageStatus(stage.id, privacyCase)
          const viewing = viewedStage === stage.id
          const clickable = status === 'done' || status === 'current'
          return (
            <li key={stage.id}>
              <button
                type="button"
                className={`privacy-flow-stage ${status}${viewing ? ' viewing' : ''}`}
                disabled={!clickable}
                onClick={() => {
                  if (status === 'current' || viewedStage === stage.id) onViewStage(null)
                  else onViewStage(stage.id)
                }}
              >
                {stage.label}
              </button>
            </li>
          )
        })}
      </ol>
      <p className="privacy-flow-hint">Toca un paso hecho para ver lo registrado. No se edita.</p>
    </div>
  )
}

function StageReview({ privacyCase, stageId, auditDispatching }) {
  if (stageId === 'close') {
    return (
      <CloseCasePreview privacyCase={privacyCase} auditDispatching={auditDispatching} />
    )
  }
  if (stageId === 'erasure' && privacyCase.erasureActa?.actaNumber) {
    return (
      <section className="privacy-stage-review">
        <h4>
          Eliminación
          <small>Solo lectura</small>
        </h4>
        <ActaPreview
          acta={privacyCase.erasureActa}
          privacyCase={privacyCase}
          residuals={privacyCase.residuals || privacyCase.erasureActa?.residuals || []}
        />
        {(() => {
          const review = stageReview(privacyCase, stageId)
          if (!review?.rows?.length) return null
          return (
            <dl className="privacy-review-list">
              {review.rows.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value || '—'}</dd>
                </div>
              ))}
            </dl>
          )
        })()}
      </section>
    )
  }

  const review = stageReview(privacyCase, stageId)
  if (!review) return null

  return (
    <section className="privacy-stage-review">
      <h4>
        {review.title}
        <small>Solo lectura</small>
      </h4>
      <dl className="privacy-review-list">
        {review.rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd
              className={
                typeof value === 'string' && value.trim().startsWith('{')
                  ? 'privacy-review-json'
                  : undefined
              }
            >
              {typeof value === 'string' && value.trim().startsWith('{') ? (
                <pre className="privacy-acta-preview-json">{formatPrettyJson(value)}</pre>
              ) : (
                value || '—'
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function formatPrettyJson(raw) {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

function ExtendFields({ privacyCase, wantsExtend, setWantsExtend, extendReason, setExtendReason, busy }) {
  if (privacyCase.extension?.grantedAt) return null
  return (
    <>
      <label className="privacy-field">
        <span>{EXTEND_COPY.fieldLabel}</span>
        <small className="privacy-field-hint">{EXTEND_COPY.intro}</small>
        <select
          value={wantsExtend ? 'yes' : 'no'}
          disabled={busy}
          onChange={(e) => {
            const yes = e.target.value === 'yes'
            setWantsExtend(yes)
            if (!yes) setExtendReason('')
          }}
        >
          <option value="no">{EXTEND_COPY.noneLabel}</option>
          <option value="yes">{EXTEND_COPY.yesLabel}</option>
        </select>
      </label>
      {wantsExtend ? (
        <>
          <StepInstructions items={EXTEND_COPY.instructions} />
          <ChoiceField
            label={EXTEND_COPY.reasonLabel}
            required
            options={EXTEND_COPY.options}
            value={extendReason}
            onChange={setExtendReason}
            disabled={busy}
          />
        </>
      ) : null}
    </>
  )
}

/**
 * Qué pidió el titular, qué se resolvió hacer y qué le toca al equipo. Encabeza
 * la ficha porque es lo primero que hay que saber para trabajar el caso.
 */
function RequestCard({ privacyCase }) {
  const copy = requestCopy(privacyCase.type)
  const request = privacyCase.request || {}
  const erasure = ERASURE_TYPES.includes(privacyCase.type)
  return (
    <section className="privacy-request-card">
      <h4>
        {copy.right}
        <small>{copy.summary}</small>
      </h4>
      <dl className="privacy-review-list">
        <div>
          <dt>{copy.whatLabel}</dt>
          <dd>{request.detail || 'Sin registrar'}</dd>
        </div>
        <div>
          <dt>{copy.targetLabel}</dt>
          <dd>{request.target || '—'}</dd>
        </div>
        <div>
          <dt>{copy.planLabel}</dt>
          <dd>
            {request.plannedAction ||
              (erasure ? 'Se resuelve en el paso Eliminación' : 'Por definir')}
            {request.plannedAt ? (
              <small className="privacy-muted">
                {[request.plannedByEmail, formatDateTime(request.plannedAt)]
                  .filter(Boolean)
                  .join(' · ')}
              </small>
            ) : null}
          </dd>
        </div>
      </dl>
      {copy.team.length ? (
        <div className="privacy-request-team">
          <span className="privacy-detail-label">Qué tiene que hacer el equipo</span>
          <ul>
            {copy.team.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

/** Resumen del expediente listo para cerrar (o ya cerrado): respuesta + acta si aplica. */
function CloseCasePreview({ privacyCase, auditDispatching = false }) {
  const resolution = privacyCase.resolution || {}
  const copy = requestCopy(privacyCase.type)
  const hasActa = Boolean(privacyCase.erasureActa?.actaNumber)
  const pendingAudit = Number(privacyCase.pendingAuditEvents || 0)
  const previewJson = formatCloseCasePreview(privacyCase)
  let responsePretty = ''
  if (resolution.responseJson) {
    try {
      responsePretty = JSON.stringify(JSON.parse(resolution.responseJson), null, 2)
    } catch {
      responsePretty = resolution.responseJson
    }
  }

  return (
    <section className="privacy-stage-review privacy-close-preview">
      <h4>
        Cierre del expediente
        <small>{privacyCase.status === 'CLOSED' ? 'Solo lectura' : 'Revisar antes de cerrar'}</small>
      </h4>
      <p className="privacy-step-intro">
        {privacyCase.status === 'CLOSED'
          ? 'Registro completo del caso: respuesta al titular'
          : 'La respuesta ya salió. Revisar el registro antes de cerrar el expediente'}
        {hasActa ? ' y el acta de eliminación.' : '.'}
      </p>

      <div className="privacy-acta-preview">
        <div className="privacy-acta-preview-summary">
          <p className="privacy-instructivo-title">Resumen</p>
          <ul className="privacy-example-list">
            <li>
              <strong>
                {privacyCase.caseCode} · {CASE_TYPE_LABELS[privacyCase.type] || privacyCase.type}
              </strong>
              <span className="privacy-muted"> · {caseStatusLabel(privacyCase)}</span>
            </li>
            <li>
              Titular: {privacyCase.subject?.name || privacyCase.subject?.email || '—'}
              {privacyCase.subject?.email && privacyCase.subject?.name
                ? ` (${privacyCase.subject.email})`
                : ''}
            </li>
            {privacyCase.companyName || privacyCase.companyId ? (
              <li>Empresa: {privacyCase.companyName || privacyCase.companyId}</li>
            ) : null}
            {privacyCase.request?.plannedAction ? (
              <li>
                {copy.planLabel}: {privacyCase.request.plannedAction}
              </li>
            ) : null}
            {resolution.respondedAt ? (
              <li>
                Respondido: {formatDateTime(resolution.respondedAt)}
                {resolution.responseTo ? ` → ${resolution.responseTo}` : ''}
                {resolution.outcome ? ` · ${resolution.outcome}` : ''}
              </li>
            ) : null}
            {resolution.deniedReason ? <li>Fundamento: {resolution.deniedReason}</li> : null}
            {pendingAudit > 0 ? (
              <li className="privacy-field-progress">
                Enviando bitácora a S3…
                {pendingAudit > 1 ? ` (${pendingAudit} eventos)` : ''}
              </li>
            ) : null}
          </ul>

          {responsePretty ? (
            <>
              <p className="privacy-instructivo-title">Respuesta al titular (JSON)</p>
              <pre className="privacy-acta-preview-json privacy-acta-preview-json--compact">
                {responsePretty}
              </pre>
            </>
          ) : null}
        </div>

        {hasActa ? (
          <ActaPreview
            acta={privacyCase.erasureActa}
            privacyCase={privacyCase}
            residuals={privacyCase.residuals || privacyCase.erasureActa?.residuals || []}
          />
        ) : null}

        <label className="privacy-field">
          <FieldLabel>JSON completo del expediente (cierre)</FieldLabel>
          <small className="privacy-field-hint">
            Incluye solicitud, respuesta
            {hasActa ? ' y acta de eliminación' : ''}. Es lo que se revisa antes de cerrar.
          </small>
          <pre className="privacy-acta-preview-json" tabIndex={0}>
            {previewJson || '—'}
          </pre>
        </label>
      </div>
    </section>
  )
}

/** Ficha del caso. El detalle vive aquí para no perder el contexto de la bandeja. */
function CaseDetail({
  privacyCase,
  onAction,
  busy,
  actionError,
  onClose,
  auditDispatching = false,
  onFlushAudit,
}) {
  const [holdReason, setHoldReason] = useState('')
  const [wantsExtend, setWantsExtend] = useState(false)
  const [extendReason, setExtendReason] = useState('')
  const [identityMethod, setIdentityMethod] = useState('')
  const [responseBody, setResponseBody] = useState('')
  const [responseSubject, setResponseSubject] = useState('')
  const [responseTo, setResponseTo] = useState(privacyCase?.subject?.email || '')
  const [deniedReason, setDeniedReason] = useState('')
  const [plannedAction, setPlannedAction] = useState('')
  const [requestDetail, setRequestDetail] = useState('')
  const [requestTarget, setRequestTarget] = useState('')
  const [viewedStage, setViewedStage] = useState(null)
  const [footerHost, setFooterHost] = useState(null)
  const [revealedBlockCommands, setRevealedBlockCommands] = useState(null)
  const [revealedPortabilityCommands, setRevealedPortabilityCommands] = useState(null)
  const [rectificationForm, setRectificationForm] = useState(() =>
    emptyRectificationEvidenceForm(privacyCase),
  )
  const [rectificationJson, setRectificationJson] = useState(() =>
    rectificationEvidenceJsonFromForm(emptyRectificationEvidenceForm(privacyCase)),
  )
  const [showRectificationJson, setShowRectificationJson] = useState(false)
  const [respondValidationError, setRespondValidationError] = useState('')

  useEffect(() => {
    // Al cambiar de caso (o al pasar a cierre tras responder), no quedar en un paso viejo.
    setViewedStage(null)
    setRevealedBlockCommands(null)
    setRevealedPortabilityCommands(null)
    setRespondValidationError('')
    const seed = emptyRectificationEvidenceForm(privacyCase)
    setRectificationForm(seed)
    setRectificationJson(rectificationEvidenceJsonFromForm(seed))
    setShowRectificationJson(false)
  }, [privacyCase?.caseCode, privacyCase?.status, privacyCase?.resolution?.respondedAt])

  const copy = requestCopy(privacyCase.type)
  const isTerminal = TERMINAL_STATUSES.includes(privacyCase.status)
  const next = nextCaseAction(privacyCase)
  const holdPending = Boolean(privacyCase.hold?.requestedAt) && privacyCase.hold?.status === 'NONE'
  const holdActive = privacyCase.hold?.status === 'ACTIVE'
  const isBlock = BLOCK_CASE_TYPES.includes(privacyCase.type)
  const isPortability = PORTABILITY_CASE_TYPES.includes(privacyCase.type)
  const isRectification = RECTIFICATION_CASE_TYPES.includes(privacyCase.type)
  const rectificationParsed = isRectification
    ? parseRectificationEvidenceJsonClient(rectificationJson)
    : { ok: true }
  const showAck = !isTerminal && next?.id === 'ack'
  const showVerify = !isTerminal && next?.id === 'verify'
  const showErasure =
    !isTerminal &&
    ERASURE_TYPES.includes(privacyCase.type) &&
    Boolean(privacyCase.subject?.identityVerifiedAt)
  const showHold = !isTerminal && (holdPending || holdActive)
  const showPlan = !isTerminal && next?.id === 'plan'
  const showBlockPlaybook =
    isBlock &&
    !isTerminal &&
    Boolean(privacyCase.subject?.identityVerifiedAt) &&
    (showHold || showPlan || next?.id === 'respond' || holdActive)
  const blockPlaybook = showBlockPlaybook ? blockCasePlaybook() : null
  const showPortabilityPlaybook =
    isPortability &&
    !isTerminal &&
    Boolean(privacyCase.subject?.identityVerifiedAt) &&
    (showPlan || next?.id === 'respond')
  const portabilityPlaybook = showPortabilityPlaybook ? portabilityCasePlaybook() : null
  const showRespond = !isTerminal && next?.id === 'respond'
  const showClose = next?.id === 'close'
  const pendingAudit = Number(privacyCase.pendingAuditEvents || 0)
  const hasPendingAudit = pendingAudit > 0
  const showExtend = showAck && !privacyCase.extension?.grantedAt
  const reviewing = Boolean(
    viewedStage && caseFlowStageStatus(viewedStage, privacyCase) === 'done',
  )

  useEffect(() => {
    if (!showClose || privacyCase.status === 'CLOSED' || !hasPendingAudit) return undefined
    if (typeof onFlushAudit === 'function') void onFlushAudit()
    const timer = setInterval(() => {
      if (typeof onFlushAudit === 'function') void onFlushAudit()
    }, 4000)
    return () => clearInterval(timer)
  }, [
    showClose,
    privacyCase.status,
    privacyCase.caseCode,
    hasPendingAudit,
    onFlushAudit,
  ])

  const applyExtensionThen = async (thenAction) => {
    if (extendReason.trim() && !privacyCase.extension?.grantedAt) {
      const ok = await onAction('extend', {
        input: { caseCode: privacyCase.caseCode, reason: extendReason.trim() },
      })
      if (!ok) return
    }
    await thenAction()
  }

  const savePlan = () => {
    const input = {
      caseCode: privacyCase.caseCode,
      plannedAction: plannedAction.trim(),
    }
    if (!privacyCase.request?.detail && requestDetail.trim()) {
      input.detail = requestDetail.trim()
    }
    if (!privacyCase.request?.target && requestTarget.trim()) {
      input.target = requestTarget.trim()
    }
    return onAction('updateRequest', { input })
  }

  const respondMissingFields = (mode) => {
    const missing = []
    if (isRectification && mode !== 'deny' && !rectificationParsed.ok) {
      missing.push('la confirmación de rectificación (JSON)')
    }
    if (!responseTo.trim()) missing.push('el destinatario')
    if (!responseBody.trim()) missing.push('el contenido íntegro de la respuesta')
    if ((mode === 'deny' || mode === 'partial') && !deniedReason.trim()) {
      missing.push('el fundamento')
    }
    return missing
  }

  const respondValidationMessage = (mode) => {
    const missing = respondMissingFields(mode)
    if (!missing.length) return ''
    if (missing.length === 1) {
      return `Completa el campo obligatorio: ${missing[0]}.`
    }
    const last = missing[missing.length - 1]
    const head = missing.slice(0, -1).join(', ')
    return `Completa los campos obligatorios: ${head} y ${last}.`
  }

  const responseJsonFor = (outcome, withClaim) =>
    buildResponseAuditJson({
      to: responseTo,
      subject: responseSubject,
      body: responseBody,
      outcome,
      deniedReason,
      claimNoticeGiven: withClaim,
    })

  const resolveWithRectification = (partial) => {
    const mode = partial ? 'partial' : 'grant'
    const message = respondValidationMessage(mode)
    if (message) {
      setRespondValidationError(message)
      return false
    }
    setRespondValidationError('')
    const input = {
      caseCode: privacyCase.caseCode,
      responseJson: responseJsonFor(partial ? 'PARTIAL' : 'GRANTED', partial),
      responseTo: responseTo.trim(),
    }
    if (partial) {
      input.partial = true
      input.deniedReason = deniedReason.trim()
    }
    if (isRectification) {
      input.rectificationJson = rectificationParsed.text || rectificationJson.trim()
    }
    return onAction('resolve', { input })
  }

  const denyWithValidation = () => {
    const message = respondValidationMessage('deny')
    if (message) {
      setRespondValidationError(message)
      return false
    }
    setRespondValidationError('')
    return onAction('deny', {
      input: {
        caseCode: privacyCase.caseCode,
        deniedReason: deniedReason.trim(),
        responseJson: responseJsonFor('DENIED', true),
        responseTo: responseTo.trim(),
      },
    })
  }

  /** Un solo botón de avance por paso: el pie de la ficha no decide, sólo ejecuta. */
  const footerStep = showAck
    ? {
        label: 'Siguiente paso',
        onConfirm: () =>
          applyExtensionThen(() => onAction('ack', { caseCode: privacyCase.caseCode })),
        disabled: wantsExtend && !extendReason.trim(),
      }
    : showVerify
      ? {
          label: 'Siguiente paso',
          onConfirm: () =>
            onAction('verify', {
              input: { caseCode: privacyCase.caseCode, method: identityMethod.trim() },
            }),
          disabled: !identityMethod.trim(),
        }
      : showPlan
        ? {
            label: 'Guardar el plan',
            onConfirm: savePlan,
            disabled:
              !plannedAction.trim() ||
              (!privacyCase.request?.detail && !requestDetail.trim()),
          }
        : null

  const caseFooter =
    privacyCase.status === 'CLOSED' ? (
      <StepFooter confirmLabel="Cerrar vista" onConfirm={onClose} />
    ) : reviewing ? (
      <StepFooter
        onCancel={onClose}
        confirmLabel="Volver al paso actual"
        onConfirm={() => setViewedStage(null)}
      />
    ) : showClose ? (
      <StepFooter
        onCancel={onClose}
        cancelLabel="Salir"
        confirmLabel={
          pendingAudit > 0
            ? auditDispatching
              ? 'Enviando bitácora…'
              : 'Esperando bitácora…'
            : 'Cerrar expediente'
        }
        onConfirm={() => onAction('close', { caseCode: privacyCase.caseCode })}
        confirmDisabled={pendingAudit > 0}
        busy={busy || (pendingAudit > 0 && auditDispatching)}
      />
    ) : !showErasure ? (
      <StepFooter
        onCancel={onClose}
        confirmLabel={footerStep?.label}
        onConfirm={footerStep?.onConfirm}
        confirmDisabled={Boolean(footerStep?.disabled)}
        busy={busy}
      />
    ) : null

  return (
    <div className="privacy-detail">
      <div className="privacy-detail-scroll">
      <div className="privacy-detail-grid">
        <div>
          <span className="privacy-detail-label">Titular</span>
          <strong>{privacyCase.subject?.name || privacyCase.subject?.email}</strong>
          <div className="privacy-muted">{privacyCase.subject?.email}</div>
          {privacyCase.subject?.rut ? (
            <div className="privacy-muted">{privacyCase.subject.rut}</div>
          ) : null}
        </div>
        <div>
          <span className="privacy-detail-label">Derecho ejercido</span>
          <strong>{CASE_TYPE_LABELS[privacyCase.type] || privacyCase.type}</strong>
          <div className="privacy-muted">
            Canal: {CHANNEL_LABELS[privacyCase.channel] || privacyCase.channel}
          </div>
        </div>
        <div>
          <span className="privacy-detail-label">Empresa</span>
          <strong>{privacyCase.companyName || privacyCase.companyId || '—'}</strong>
          {privacyCase.companyAlias ? (
            <div className="privacy-muted">{privacyCase.companyAlias}</div>
          ) : null}
        </div>
        <div>
          <span className="privacy-detail-label">Recibido</span>
          <strong>{formatDateTime(privacyCase.receivedAt)}</strong>
          <div className="privacy-muted">
            Acuse: {privacyCase.ackAt ? formatDateTime(privacyCase.ackAt) : 'pendiente'}
          </div>
        </div>
        <div>
          <span className="privacy-detail-label">Vence</span>
          <strong>{formatDate(privacyCase.effectiveDueAt)}</strong>
          <div className="privacy-muted">{formatDueLabel(privacyCase)}</div>
          {privacyCase.extension?.grantedAt ? (
            <div className="privacy-muted">Prórroga usada</div>
          ) : null}
        </div>
        <div>
          <span className="privacy-detail-label">Identidad</span>
          <strong>
            {privacyCase.subject?.identityVerifiedAt ? 'Verificada' : 'Sin verificar'}
          </strong>
          {privacyCase.subject?.identityMethod ? (
            <div className="privacy-muted">{privacyCase.subject.identityMethod}</div>
          ) : null}
        </div>
      </div>

      <RequestCard privacyCase={privacyCase} />

      {privacyCase.escalation?.contactEmails?.length ? (
        <div className="privacy-note">
          Escalado el {formatDateTime(privacyCase.escalation.sentAt)} (
          {privacyCase.escalation.contactSource}) a:
          <ul className="privacy-contact-list">
            {privacyCase.escalation.contactEmails.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {privacyCase.resolution?.respondedAt ? (
        <div className="privacy-note">
          Respondido el {formatDateTime(privacyCase.resolution.respondedAt)} a{' '}
          {privacyCase.resolution.responseTo}.{' '}
          {privacyCase.resolution.deniedReason
            ? `Fundamento: ${privacyCase.resolution.deniedReason}`
            : ''}
        </div>
      ) : null}

      <CaseFlow
        privacyCase={privacyCase}
        viewedStage={viewedStage}
        onViewStage={setViewedStage}
      />

      {actionError ? <div className="privacy-note error">{actionError}</div> : null}

      {reviewing ? (
        <div className="privacy-action-blocks">
          <StageReview
            privacyCase={privacyCase}
            stageId={viewedStage}
            auditDispatching={auditDispatching}
          />
        </div>
      ) : showClose || privacyCase.status === 'CLOSED' ? (
        <div className="privacy-action-blocks">
          <CloseCasePreview
            privacyCase={privacyCase}
            auditDispatching={auditDispatching}
          />
        </div>
      ) : !isTerminal ? (
        <div className="privacy-action-blocks">
          {showAck ? (
            <section>
              <h4>
                {ACK_COPY.title}
                <small>Paso actual</small>
              </h4>
              <p className="privacy-step-intro">
                {`${copy.summary} ${ACK_COPY.intro}`}
              </p>
              <StepInstructions items={caseStageInstructions('ack', privacyCase)} />
              {showExtend ? (
                <ExtendFields
                  privacyCase={privacyCase}
                  wantsExtend={wantsExtend}
                  setWantsExtend={setWantsExtend}
                  extendReason={extendReason}
                  setExtendReason={setExtendReason}
                  busy={busy}
                />
              ) : null}
            </section>
          ) : null}

          {showVerify ? (
            <section>
              <h4>
                {IDENTITY_VERIFICATION_COPY.title}
                <small>Paso actual</small>
              </h4>
              <p className="privacy-step-intro">
                {[copy.identityWhy, IDENTITY_VERIFICATION_COPY.intro]
                  .filter(Boolean)
                  .join(' ')}
              </p>
              <StepInstructions items={caseStageInstructions('verify', privacyCase)} />
              <ChoiceField
                label={IDENTITY_VERIFICATION_COPY.fieldLabel}
                options={IDENTITY_VERIFICATION_COPY.options}
                value={identityMethod}
                onChange={setIdentityMethod}
                disabled={busy}
                required
              />
            </section>
          ) : null}

          {showHold ? (
          <section>
            <h4>
              Bloqueo temporal
              <small>
                {holdPending
                  ? 'Hay que decidir en 2 días hábiles'
                  : 'Activo · el titular pidió congelar el tratamiento'}
              </small>
            </h4>
            {holdPending ? (
              <div className="privacy-inline-form column">
                <StepInstructions items={caseStageInstructions('hold', privacyCase)} />
                <ChoiceField
                  label={TEMPORAL_HOLD_DECIDE_COPY.fieldLabel}
                  options={TEMPORAL_HOLD_DECIDE_COPY.options}
                  value={holdReason}
                  onChange={setHoldReason}
                  disabled={busy}
                  required
                />
                <div className="privacy-actions">
                <button
                  className="btn btn-success btn-sm"
                  disabled={busy || !holdReason.trim()}
                  onClick={() =>
                    onAction('decideHold', {
                      input: {
                        caseCode: privacyCase.caseCode,
                        granted: true,
                        reason: holdReason.trim(),
                      },
                    })
                  }
                >
                  Conceder
                </button>
                <button
                  className="btn btn-outline-danger btn-sm"
                  disabled={busy || !holdReason.trim()}
                  onClick={() =>
                    onAction('decideHold', {
                      input: {
                        caseCode: privacyCase.caseCode,
                        granted: false,
                        reason: holdReason.trim(),
                      },
                    })
                  }
                >
                  Rechazar
                </button>
                </div>
              </div>
            ) : (
              <div className="privacy-actions">
                <button
                  className="btn btn-outline btn-sm"
                  disabled={busy}
                  onClick={() => onAction('liftHold', { caseCode: privacyCase.caseCode })}
                >
                  Levantar bloqueo
                </button>
              </div>
            )}
          </section>
          ) : null}

          {showBlockPlaybook ? (
            <section>
              <h4>
                Comandos de congelamiento
                <small>Art. 8 ter · solo DPO puede revelarlos</small>
              </h4>
              <p className="privacy-step-intro">
                Congelar el tratamiento (jobs, colas, flags). No borrar ni anonimizar: eso
                corresponde a una solicitud de supresión.
              </p>
              <StepPlaybook
                playbook={blockPlaybook}
                revealedCommands={revealedBlockCommands}
                busy={busy}
                onReveal={() =>
                  onAction('revealPlaybook', {
                    input: {
                      caseCode: privacyCase.caseCode,
                      stepCode: BLOCK_PLAYBOOK_STEP,
                    },
                    onRevealed: (revealed) => {
                      setRevealedBlockCommands(revealed?.commands || [])
                    },
                  })
                }
              />
            </section>
          ) : null}

          {showPortabilityPlaybook ? (
            <section>
              <h4>
                Comandos de extracción
                <small>Art. 9 · guía · solo DPO puede revelarlos</small>
              </h4>
              <p className="privacy-step-intro">
                Plantillas para inventariar, exportar (CSV/JSON) y entregar. Adapta
                colecciones y filtros al plan del expediente; no son un script único
                para todos los casos.
              </p>
              <StepPlaybook
                playbook={portabilityPlaybook}
                revealedCommands={revealedPortabilityCommands}
                busy={busy}
                onReveal={() =>
                  onAction('revealPlaybook', {
                    input: {
                      caseCode: privacyCase.caseCode,
                      stepCode: PORTABILITY_PLAYBOOK_STEP,
                    },
                    onRevealed: (revealed) => {
                      setRevealedPortabilityCommands(revealed?.commands || [])
                    },
                  })
                }
              />
            </section>
          ) : null}

          {showPlan ? (
            <section>
              <h4>
                {copy.planLabel}
                <small>Paso actual</small>
              </h4>
              <p className="privacy-step-intro">
                Deja por escrito qué se va a hacer con sus datos: es lo que el equipo ejecuta
                y lo que la respuesta le cuenta al titular.
              </p>
              <StepInstructions items={caseStageInstructions('plan', privacyCase)} />
              {!privacyCase.request?.detail ? (
                <ChoiceField
                  label={copy.whatLabel}
                  hint="Este caso se cargó sin registrar qué pide. Complétalo antes de seguir."
                  options={copy.whatOptions}
                  value={requestDetail}
                  onChange={setRequestDetail}
                  disabled={busy}
                  required
                />
              ) : null}
              {!privacyCase.request?.target ? (
                <label className="privacy-field">
                  <span>{copy.targetLabel}</span>
                  <input
                    placeholder={copy.targetHint}
                    value={requestTarget}
                    onChange={(e) => setRequestTarget(e.target.value)}
                    disabled={busy}
                  />
                </label>
              ) : null}
              <ChoiceField
                label={copy.planLabel}
                options={copy.planOptions}
                value={plannedAction}
                onChange={setPlannedAction}
                disabled={busy}
                required
              />
              {copy.team.length ? (
                <ul className="privacy-example-list">
                  {copy.team.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ) : null}

          {showErasure ? (
            <ErasurePanel
              privacyCase={privacyCase}
              onAction={onAction}
              busy={busy}
              onClose={onClose}
              footerHost={footerHost}
            />
          ) : null}

          {showRespond ? (
          <section>
            <h4>
              Respuesta al titular
              <small>Qué se le envió y a quién</small>
            </h4>
            {privacyCase.request?.plannedAction ? (
              <p className="privacy-step-intro">
                {`Se resolvió: ${privacyCase.request.plannedAction}. ${copy.respondHint}`}
              </p>
            ) : (
              <p className="privacy-step-intro">{copy.respondHint}</p>
            )}
            <StepInstructions items={caseStageInstructions('respond', privacyCase)} />
            <div className="privacy-inline-form column">
              {isRectification ? (
                <div className="privacy-evidence-builder">
                  <div className="privacy-field">
                    <FieldLabel required>Confirmación de rectificación (JSON)</FieldLabel>
                    <small className="privacy-field-hint">
                      Constancia de que el dato pedido quedó modificado en origen. Sin esto no
                      se puede conceder. No un link.
                    </small>
                  </div>
                  {!showRectificationJson ? (
                    <>
                      <label className="privacy-field">
                        <FieldLabel required>Dato / campo corregido</FieldLabel>
                        <input
                          placeholder="Ej.: users.rut · companies.address"
                          value={rectificationForm.field}
                          onChange={(e) => {
                            const nextForm = { ...rectificationForm, field: e.target.value }
                            setRectificationForm(nextForm)
                            setRectificationJson(rectificationEvidenceJsonFromForm(nextForm))
                          }}
                          disabled={busy}
                          required
                        />
                      </label>
                      <label className="privacy-field">
                        <span>Valor anterior</span>
                        <input
                          placeholder="Lo que figuraba antes"
                          value={rectificationForm.before}
                          onChange={(e) => {
                            const nextForm = { ...rectificationForm, before: e.target.value }
                            setRectificationForm(nextForm)
                            setRectificationJson(rectificationEvidenceJsonFromForm(nextForm))
                          }}
                          disabled={busy}
                        />
                      </label>
                      <label className="privacy-field">
                        <FieldLabel required>Valor nuevo</FieldLabel>
                        <input
                          placeholder="Valor correcto tras la corrección"
                          value={rectificationForm.after}
                          onChange={(e) => {
                            const nextForm = { ...rectificationForm, after: e.target.value }
                            setRectificationForm(nextForm)
                            setRectificationJson(rectificationEvidenceJsonFromForm(nextForm))
                          }}
                          disabled={busy}
                          required
                        />
                      </label>
                      <label className="privacy-field">
                        <span>Colección / sistema</span>
                        <input
                          placeholder="Ej.: users · Mongo · portal"
                          value={rectificationForm.collection}
                          onChange={(e) => {
                            const nextForm = {
                              ...rectificationForm,
                              collection: e.target.value,
                            }
                            setRectificationForm(nextForm)
                            setRectificationJson(rectificationEvidenceJsonFromForm(nextForm))
                          }}
                          disabled={busy}
                        />
                      </label>
                      <label className="privacy-field">
                        <span>Nota</span>
                        <input
                          placeholder="Opcional: aviso a terceros, ticket, etc."
                          value={rectificationForm.note}
                          onChange={(e) => {
                            const nextForm = { ...rectificationForm, note: e.target.value }
                            setRectificationForm(nextForm)
                            setRectificationJson(rectificationEvidenceJsonFromForm(nextForm))
                          }}
                          disabled={busy}
                        />
                      </label>
                    </>
                  ) : (
                    <label className="privacy-field">
                      <FieldLabel required>JSON</FieldLabel>
                      <textarea
                        className="privacy-letter"
                        value={rectificationJson}
                        onChange={(e) => {
                          setRectificationJson(e.target.value)
                          setRectificationForm(
                            rectificationEvidenceFormFromJson(e.target.value, privacyCase),
                          )
                        }}
                        disabled={busy}
                        rows={10}
                      />
                    </label>
                  )}
                  <div className="privacy-actions">
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={busy}
                      onClick={() => setShowRectificationJson((v) => !v)}
                    >
                      {showRectificationJson ? 'Ver formulario' : 'Editar JSON'}
                    </button>
                  </div>
                  {!rectificationParsed.ok ? (
                    <p className="privacy-muted small">{rectificationParsed.error}</p>
                  ) : (
                    <p className="privacy-muted small">
                      Se guardará con modified: true · {rectificationForm.field || 'campo'} →{' '}
                      {rectificationForm.after || '…'}
                    </p>
                  )}
                </div>
              ) : null}
              <label className="privacy-field">
                <FieldLabel required>Destinatario de la respuesta</FieldLabel>
                <input
                  placeholder="correo del titular"
                  value={responseTo}
                  onChange={(e) => {
                    setResponseTo(e.target.value)
                    setRespondValidationError('')
                  }}
                  required
                />
              </label>
              <label className="privacy-field">
                <span>Asunto</span>
                <input
                  placeholder="Ej.: Respuesta a su solicitud de privacidad"
                  value={responseSubject}
                  onChange={(e) => setResponseSubject(e.target.value)}
                />
              </label>
              <label className="privacy-field">
                <FieldLabel required>Contenido íntegro de la respuesta</FieldLabel>
                <small className="privacy-field-hint">
                  El texto que se envió al titular (Art. 11). Se guarda como JSON en el
                  expediente y, si hay acta, se inserta en el JSON del acta. No un link.
                </small>
                <textarea
                  className="privacy-letter"
                  placeholder="Texto enviado al titular"
                  value={responseBody}
                  onChange={(e) => {
                    setResponseBody(e.target.value)
                    setRespondValidationError('')
                  }}
                  required
                />
              </label>
              <ChoiceField
                label={DENIAL_REASON_COPY.fieldLabel}
                hint={DENIAL_REASON_COPY.intro}
                options={DENIAL_REASON_COPY.options}
                value={deniedReason}
                onChange={(value) => {
                  setDeniedReason(value)
                  setRespondValidationError('')
                }}
                disabled={busy}
              />
              <StepInstructions items={DENIAL_REASON_COPY.instructions} title="Antes de denegar" />
              {respondValidationError ? (
                <div className="privacy-note warning" role="alert">
                  {respondValidationError}
                </div>
              ) : null}
              <div className="privacy-actions">
                <button
                  className="btn btn-primary btn-sm"
                  disabled={busy}
                  onClick={() => resolveWithRectification(false)}
                >
                  {copy.grantLabel}
                </button>
                <button
                  className="btn btn-outline-warning btn-sm"
                  disabled={busy}
                  onClick={() => resolveWithRectification(true)}
                >
                  Conceder en parte
                </button>
                <button
                  className="btn btn-outline-danger btn-sm"
                  disabled={busy}
                  onClick={denyWithValidation}
                >
                  Denegar
                </button>
              </div>
              <p className="privacy-muted small">
                Conceder en parte y denegar exigen rol DPO, la contraseña del portal y abren el plazo de
                reclamo del titular ante la Agencia.
                {isRectification
                  ? ' En rectificación, conceder exige el JSON de confirmación del cambio.'
                  : ''}
              </p>
            </div>
          </section>
          ) : null}
        </div>
      ) : null}
      </div>

      <div className="privacy-detail-footer">
        {caseFooter}
        <div className="privacy-detail-footer-slot" ref={setFooterHost} />
      </div>
    </div>
  )
}

function CreateCaseForm({ form, setForm }) {
  const update = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))
  const copy = requestCopy(form.type)
  return (
    <div className="privacy-form">
      <label className="privacy-field">
        <FieldLabel required>Derecho ejercido</FieldLabel>
        <select
          value={form.type}
          onChange={(e) =>
            // El vocabulario de "qué pide" cambia con el derecho: lo anterior ya no aplica.
            setForm((prev) => ({
              ...prev,
              type: e.target.value,
              requestDetail: '',
              requestTarget: '',
            }))
          }
          required
        >
          {Object.keys(CASE_TYPE_LABELS).map((value) => (
            <option key={value} value={value}>
              {caseTypeSelectLabel(value)}
            </option>
          ))}
        </select>
        <small className="privacy-field-hint">{copy.summary}</small>
      </label>

      <ChoiceField
        key={`what-${form.type}`}
        label={copy.whatLabel}
        required
        options={copy.whatOptions}
        value={form.requestDetail}
        onChange={(value) => setForm((prev) => ({ ...prev, requestDetail: value }))}
      />

      <label className="privacy-field">
        <span>{copy.targetLabel}</span>
        <input
          value={form.requestTarget}
          onChange={update('requestTarget')}
          placeholder={copy.targetHint}
        />
        <small className="privacy-field-hint">
          En palabras del titular, sin pegar documentos ni datos de terceros.
        </small>
      </label>

      <label className="privacy-field">
        <FieldLabel required>Correo del titular</FieldLabel>
        <input
          type="email"
          value={form.email}
          onChange={update('email')}
          placeholder="titular@ejemplo.cl"
          required
        />
      </label>

      <div className="privacy-field-row">
        <label className="privacy-field">
          <span>RUT</span>
          <input value={form.rut} onChange={update('rut')} placeholder="12.345.678-5" />
          <small className="privacy-field-hint">Si viene, manda sobre el correo</small>
        </label>

        <label className="privacy-field">
          <span>Nombre</span>
          <input value={form.name} onChange={update('name')} placeholder="Nombre del titular" />
        </label>
      </div>

      <label className="privacy-field">
        <FieldLabel required>ID de la empresa involucrada</FieldLabel>
        <input
          value={form.companyId}
          onChange={update('companyId')}
          placeholder="ObjectId de la empresa"
          required
        />
      </label>

      {ERASURE_TYPES.includes(form.type) ? (
        <>
          <label className="privacy-field">
            <FieldLabel required>Alcance A-12</FieldLabel>
            <select value={form.scope} onChange={update('scope')} required>
              {Object.keys(SCOPE_LABELS).map((value) => (
                <option key={value} value={value}>
                  {scopeSelectLabel(value)}
                </option>
              ))}
            </select>
          </label>
          {form.scope === 'USER' ? (
            <label className="privacy-field">
              <span>ID del usuario titular</span>
              <input value={form.userId} onChange={update('userId')} placeholder="ObjectId del usuario" />
            </label>
          ) : null}
        </>
      ) : null}

      <label className="privacy-field">
        <span>Fecha de recepción</span>
        <input type="datetime-local" value={form.receivedAt} onChange={update('receivedAt')} />
        <small className="privacy-field-hint">
          El plazo de 30 días corre desde que llegó el correo, no desde que se carga aquí. Si se
          deja vacío se usa el momento actual.
        </small>
      </label>
    </div>
  )
}

export function PrivacyList() {
  const location = useLocation()
  const [statusFilter, setStatusFilter] = useState('OPEN')
  const [typeFilter, setTypeFilter] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM)
  const [actionError, setActionError] = useState('')
  const [passwordPrompt, setPasswordPrompt] = useState(null)
  const [dpoPassword, setDpoPassword] = useState('')
  const dpoPasswordInputRef = useRef(null)
  const [passwordBusy, setPasswordBusy] = useState(false)
  const [intakeOverlay, setIntakeOverlay] = useState(null)

  // Prefill desde «Offboarding usuario» (`/privacidad/localizar`).
  useEffect(() => {
    const prefill = location.state?.createPrefill
    if (!prefill) return
    setCreateForm({
      ...EMPTY_CREATE_FORM,
      email: prefill.email || '',
      rut: prefill.rut || '',
      companyId: prefill.companyId || '',
      userId: prefill.userId || '',
      scope: prefill.userId ? 'USER' : 'COMPANY',
    })
    setCreateOpen(true)
    window.history.replaceState({}, document.title)
  }, [location.state])

  const filter = useMemo(() => {
    const value = {}
    if (statusFilter === 'OVERDUE') value.onlyOverdue = true
    else if (statusFilter === 'DUE_SOON') value.dueWithinDays = 7
    else if (statusFilter === 'OPEN') value.onlyOpen = true
    else if (statusFilter === 'AWAITING_ACK') value.awaitingAck = true
    else if (statusFilter === 'PENDING_CLOSE') value.pendingClose = true
    else if (statusFilter === 'MISSING_COMPANY') value.missingCompany = true
    else if (statusFilter === 'HOLDS_UNDECIDED') value.holdsUndecided = true
    else if (statusFilter === 'BLOCKS_OPEN') {
      value.onlyOpen = true
      value.type = 'BLOCK'
    }
    else if (statusFilter === 'ERASURES_OPEN') {
      value.onlyOpen = true
      value.type = 'ERASURE'
    }
    else if (statusFilter && statusFilter !== 'ALL') {
      value.statuses = [statusFilter]
    }
    // Los atajos de indicador ya fijan el derecho; el select no debe pisarlos.
    if (
      typeFilter &&
      statusFilter !== 'BLOCKS_OPEN' &&
      statusFilter !== 'ERASURES_OPEN'
    ) {
      value.type = typeFilter
    }
    if (search.trim()) value.search = search.trim()
    return value
  }, [statusFilter, typeFilter, search])

  const [{ data, fetching, error }, refetchCases] = useQuery({
    query: GET_PRIVACY_CASES,
    variables: { filter },
  })
  const [{ data: dashData }, refetchDashboard] = useQuery({ query: GET_PRIVACY_DASHBOARD })
  const [{ data: outboxData }, refetchOutbox] = useQuery({
    query: GET_PRIVACY_AUDIT_OUTBOX,
  })
  const [{ data: intakeData }, refetchIntake] = useQuery({
    query: GET_PRIVACY_EMAIL_INTAKE,
  })
  const [dispatchState, dispatchOutbox] = useMutation(DISPATCH_PRIVACY_AUDIT_OUTBOX)
  const [intakeState, runIntake] = useMutation(RUN_PRIVACY_EMAIL_INTAKE)
  const [listenState, setIntakeListening] = useMutation(SET_PRIVACY_EMAIL_INTAKE_LISTENING)

  const [ackState, acknowledgeCase] = useMutation(ACKNOWLEDGE_PRIVACY_CASE)
  const [verifyState, verifyIdentity] = useMutation(VERIFY_PRIVACY_CASE_IDENTITY)
  const [extendState, extendCase] = useMutation(EXTEND_PRIVACY_CASE)
  const [decideState, decideHold] = useMutation(DECIDE_PRIVACY_CASE_HOLD)
  const [liftState, liftHold] = useMutation(LIFT_PRIVACY_CASE_HOLD)
  const [resolveState, resolveCase] = useMutation(RESOLVE_PRIVACY_CASE)
  const [denyState, denyCase] = useMutation(DENY_PRIVACY_CASE)
  const [closeState, closeCase] = useMutation(CLOSE_PRIVACY_CASE)
  const [createState, createCase] = useMutation(CREATE_PRIVACY_CASE)
  const [conservationState, setConservationHold] = useMutation(SET_PRIVACY_CONSERVATION_HOLD)
  const [stepState, updateStep] = useMutation(UPDATE_PRIVACY_PROCEDURE_STEP)
  const [requestState, updateRequest] = useMutation(UPDATE_PRIVACY_CASE_REQUEST)
  const [addResidualState, addResidual] = useMutation(ADD_PRIVACY_RESIDUAL)
  const [clearResidualState, clearResidual] = useMutation(CLEAR_PRIVACY_RESIDUAL)
  const [generateActaState, generateActa] = useMutation(GENERATE_PRIVACY_ERASURE_ACTA)
  const [updateActaState, updateActa] = useMutation(UPDATE_PRIVACY_ERASURE_ACTA)
  const [issueActaState, issueActa] = useMutation(ISSUE_PRIVACY_ERASURE_ACTA)
  const [closeActaState, closeActa] = useMutation(CLOSE_PRIVACY_ERASURE_ACTA)
  const [revealPlaybookState, revealPlaybook] = useMutation(REVEAL_PRIVACY_ERASURE_PLAYBOOK)

  const busy = [
    ackState,
    verifyState,
    extendState,
    decideState,
    liftState,
    resolveState,
    denyState,
    closeState,
    createState,
    conservationState,
    stepState,
    requestState,
    addResidualState,
    clearResidualState,
    generateActaState,
    updateActaState,
    issueActaState,
    closeActaState,
    revealPlaybookState,
  ].some((state) => state.fetching)

  const reload = useCallback(() => {
    refetchCases({ requestPolicy: 'network-only' })
    refetchDashboard({ requestPolicy: 'network-only' })
    refetchOutbox({ requestPolicy: 'network-only' })
    refetchIntake({ requestPolicy: 'network-only' })
  }, [refetchCases, refetchDashboard, refetchOutbox, refetchIntake])

  /**
   * Drena la bitácora a S3 sin esperar el intervalo del despachador. Es lo que
   * desbloquea el cierre de un caso cuando quedaron eventos sin confirmar.
   */
  const runDispatch = useCallback(async () => {
    setActionError('')
    const result = await dispatchOutbox({})
    if (result.error) {
      setActionError(result.error.message.replace('[GraphQL] ', ''))
      return
    }
    const outcome = result.data?.dispatchPrivacyAuditOutbox
    if (outcome?.skippedReason && outcome.skippedReason !== 'ya hay una pasada en curso') {
      setActionError(outcome.skippedReason)
    }
    reload()
  }, [dispatchOutbox, reload])

  const runMailboxIntake = useCallback(async () => {
    setActionError('')
    setIntakeOverlay({ running: true, outcome: null, error: '' })
    const result = await runIntake({})
    if (result.error) {
      const message = result.error.message.replace('[GraphQL] ', '')
      setIntakeOverlay({ running: false, outcome: null, error: message })
      setActionError(message)
      return
    }
    const outcome = result.data?.runPrivacyEmailIntake
    const message = outcome?.errors?.[0] || ''
    setIntakeOverlay({ running: false, outcome: outcome || null, error: message })
    if (message) setActionError(message)
    reload()
  }, [runIntake, reload])

  const toggleMailboxListening = useCallback(async () => {
    const current = intakeData?.privacyEmailIntake
    if (!current || current.envForcedOff || listenState.fetching) return
    setActionError('')
    const result = await setIntakeListening({ listening: !current.storedListening })
    if (result.error) {
      setActionError(result.error.message.replace('[GraphQL] ', ''))
      return
    }
    refetchIntake({ requestPolicy: 'network-only' })
  }, [intakeData, listenState.fetching, setIntakeListening, refetchIntake])

  const runAction = useCallback(
    async (action, variables) => {
      setActionError('')
      if (
        privacyActionNeedsPassword(action, variables) &&
        !variables?.input?.confirmPassword
      ) {
        setPasswordPrompt({ action, variables })
        setDpoPassword('')
        return false
      }
      const runners = {
        ack: acknowledgeCase,
        verify: verifyIdentity,
        extend: extendCase,
        decideHold,
        liftHold,
        resolve: resolveCase,
        deny: denyCase,
        close: closeCase,
        setConservationHold,
        updateStep,
        updateRequest,
        addResidual,
        clearResidual,
        generateActa,
        updateActa,
        issueActa,
        closeActa,
        revealPlaybook,
      }
      const runner = runners[action]
      if (!runner) return false

      const result = await runner(
        action === 'revealPlaybook'
          ? { input: variables.input }
          : variables,
      )
      if (result.error) {
        setActionError(result.error.message.replace('[GraphQL] ', ''))
        return false
      }
      if (action === 'revealPlaybook') {
        const revealed = result.data?.revealPrivacyErasurePlaybook
        variables?.onRevealed?.(revealed)
        void dispatchOutbox({}).then((dispatchResult) => {
          if (dispatchResult.error) {
            setActionError(dispatchResult.error.message.replace('[GraphQL] ', ''))
          }
          refetchOutbox({ requestPolicy: 'network-only' })
        })
        return true
      }
      const updated = Object.values(result.data || {})[0]
      if (updated) setSelected(updated)
      reload()
      void dispatchOutbox({}).then((dispatchResult) => {
        if (dispatchResult.error) {
          setActionError(dispatchResult.error.message.replace('[GraphQL] ', ''))
        } else {
          const skipped = dispatchResult.data?.dispatchPrivacyAuditOutbox?.skippedReason
          if (skipped && skipped !== 'ya hay una pasada en curso') {
            setActionError(skipped)
          }
        }
        refetchOutbox({ requestPolicy: 'network-only' })
      })
      return true
    },
    [
      acknowledgeCase,
      verifyIdentity,
      extendCase,
      decideHold,
      liftHold,
      resolveCase,
      denyCase,
      closeCase,
      setConservationHold,
      updateStep,
      updateRequest,
      addResidual,
      clearResidual,
      generateActa,
      updateActa,
      issueActa,
      closeActa,
      revealPlaybook,
      reload,
      dispatchOutbox,
      refetchOutbox,
    ],
  )

  const closePasswordPrompt = useCallback(() => {
    setPasswordPrompt(null)
    setDpoPassword('')
  }, [])

  useEffect(() => {
    if (!passwordPrompt) return undefined
    const focusInput = () => {
      const el = dpoPasswordInputRef.current
      if (!el) return
      el.focus()
      el.select()
    }
    const raf = window.requestAnimationFrame(focusInput)
    const timer = window.setTimeout(focusInput, 50)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(timer)
    }
  }, [passwordPrompt])

  const submitDpoPassword = useCallback(async () => {
    if (!passwordPrompt || !dpoPassword.trim() || passwordBusy) return
    setPasswordBusy(true)
    setActionError('')
    try {
      const confirmPassword = await encryptAsync(dpoPassword)
      const ok = await runAction(passwordPrompt.action, {
        ...passwordPrompt.variables,
        input: {
          ...(passwordPrompt.variables?.input || {}),
          confirmPassword,
        },
      })
      if (ok) closePasswordPrompt()
    } catch (err) {
      setActionError(err?.message || 'No se pudo confirmar la contraseña')
    } finally {
      setPasswordBusy(false)
    }
  }, [passwordPrompt, dpoPassword, passwordBusy, runAction, closePasswordPrompt])

  const submitCreate = useCallback(async () => {
    setActionError('')
    const input = {
      type: createForm.type,
      controllerRole: 'ENCARGADO',
      email: createForm.email.trim(),
      channel: 'EMAIL_DPO',
    }
    if (createForm.rut.trim()) input.rut = createForm.rut.trim()
    if (createForm.name.trim()) input.name = createForm.name.trim()
    input.companyId = createForm.companyId.trim()
    input.requestDetail = createForm.requestDetail.trim()
    if (createForm.requestTarget.trim()) input.requestTarget = createForm.requestTarget.trim()
    if (ERASURE_TYPES.includes(createForm.type)) {
      input.scope = createForm.scope
      if (createForm.userId.trim()) input.userId = createForm.userId.trim()
    }
    if (createForm.receivedAt) input.receivedAt = new Date(createForm.receivedAt).toISOString()

    const result = await createCase({ input })
    if (result.error) {
      setActionError(result.error.message.replace('[GraphQL] ', ''))
      return
    }
    setCreateOpen(false)
    setCreateForm(EMPTY_CREATE_FORM)
    reload()
    void dispatchOutbox({})
  }, [createForm, createCase, reload, dispatchOutbox])

  const cases = data?.privacyCases || []
  const dash = dashData?.privacyDashboard
  const outbox = outboxData?.privacyAuditOutbox
  const outboxFailed = (outbox?.failed || 0) > 0
  const intake = intakeData?.privacyEmailIntake
  const mailboxListening = Boolean(intake?.listening)

  useEffect(() => {
    if (!selected?.caseCode) return
    const fresh = cases.find((item) => item.caseCode === selected.caseCode)
    if (!fresh) return
    if (
      fresh.pendingAuditEvents !== selected.pendingAuditEvents ||
      fresh.status !== selected.status
    ) {
      setSelected(fresh)
    }
  }, [cases, selected])

  const stats = [
    {
      label: 'Abiertos',
      value: dash?.open,
      tone: 'neutral',
      filter: 'OPEN',
      title: 'Casos aún en trabajo (sin resolución final ni cierre)',
    },
    {
      label: 'Vencidos',
      value: dash?.overdue,
      tone: 'red',
      filter: 'OVERDUE',
      title: 'Plazo legal vencido',
    },
    {
      label: 'Sin acuse',
      value: dash?.awaitingAck,
      tone: 'amber',
      filter: 'AWAITING_ACK',
      title: 'Recibidos sin acuse de recibo',
    },
    {
      label: 'Bloqueos por decidir',
      value: dash?.holdsUndecided,
      tone: 'amber',
      filter: 'HOLDS_UNDECIDED',
      title: 'Hold temporal Art. 8 ter solicitado y aún sin decidir (2 días hábiles)',
    },
    {
      label: 'Bitácora pendiente',
      value: dash?.auditOutboxPending,
      tone: 'amber',
      filter: null,
      title: 'Eventos de auditoría sin confirmar en S3 (bloquean el cierre)',
    },
    {
      label: 'Por cerrar',
      value: dash?.pendingClose,
      tone: 'neutral',
      filter: 'PENDING_CLOSE',
      title: 'Respondidos; falta el cierre formal del expediente',
    },
    {
      label: 'Supresiones abiertas',
      value: dash?.erasuresOpen,
      tone: 'neutral',
      filter: 'ERASURES_OPEN',
      title: 'Derecho de supresión en curso (únicos con acta A-12)',
    },
    {
      label: 'Sin empresa',
      value: dash?.missingCompany,
      tone: 'amber',
      filter: 'MISSING_COMPANY',
      title: 'Abiertos sin companyId (columna Empresa vacía; bitácora en unscoped)',
    },
  ]

  const applyStatFilter = (stat) => {
    if (!stat.filter) return
    setStatusFilter(stat.filter)
    if (stat.filter === 'BLOCKS_OPEN' || stat.filter === 'ERASURES_OPEN') {
      setTypeFilter('')
    }
  }

  return (
    <div className="privacy-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <ShieldIcon /> Privacidad
          </h1>
          <p className="page-subtitle">
            Cada solicitud se trabaja en este orden: acuse → identidad → plan (o eliminación,
            si el derecho lo pide) → respuesta → cierre.
          </p>
        </div>
        <div className="privacy-header-actions">
          <div className={`privacy-intake-cluster${intake?.envForcedOff ? ' is-locked' : ''}${mailboxListening ? ' is-on' : ''}`}>
            <label
              className="privacy-intake-switch"
              title={
                intake?.envForcedOff
                  ? 'Forzada a off con PRIVACY_INTAKE_DISABLED en .env'
                  : !intake?.configured
                    ? 'Faltan Gmail u OpenAI en .env'
                    : mailboxListening
                      ? `Altas desde ${intake?.mailbox || 'el buzón'} permitidas. Usá Leer buzón; sólo se crea expediente si la IA ve una solicitud ARCO.`
                      : 'Pausado: no se crean casos desde el correo'
              }
            >
              <span className="privacy-intake-switch-copy">
                <span className="privacy-intake-switch-label">
                  {mailboxListening ? 'Buzón on' : 'Buzón off'}
                </span>
                <span className="privacy-intake-switch-hint">
                  {intake?.envForcedOff
                    ? 'Forzada por .env'
                    : !intake?.configured
                      ? 'Sin credenciales'
                      : mailboxListening
                        ? 'Sólo si es ARCO'
                        : 'No crea casos'}
                </span>
              </span>
              <input
                type="checkbox"
                checked={Boolean(intake?.storedListening) && !intake?.envForcedOff}
                disabled={!intake || intake.envForcedOff || listenState.fetching}
                onChange={toggleMailboxListening}
              />
              <span className="privacy-intake-knob" aria-hidden />
            </label>
            <button
              type="button"
              className="btn btn-outline privacy-intake-read"
              disabled={intakeState.fetching || !mailboxListening || Boolean(intakeOverlay?.running)}
              onClick={runMailboxIntake}
              title={`Lee ${intake?.mailbox || 'joaquin.vila@smartcfo.cl'}, clasifica el derecho y abre el expediente.`}
            >
              <InboxIcon />
              {intakeState.fetching || intakeOverlay?.running ? 'Leyendo…' : 'Leer buzón'}
            </button>
          </div>
          <div className="privacy-header-buttons">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setGuideOpen(true)}
              title="Plazos y deberes de la Ley 21.719"
            >
              <BookIcon /> Deberes y plazos
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              <PlusIcon /> Nueva solicitud
            </button>
          </div>
        </div>
      </div>

      {intakeOverlay ? (
        <PrivacyIntakeProgress
          mailbox={intakeOverlay.outcome?.mailbox || intake?.mailbox || 'joaquin.vila@smartcfo.cl'}
          running={intakeOverlay.running}
          outcome={intakeOverlay.outcome}
          error={intakeOverlay.error}
          onClose={() => setIntakeOverlay(null)}
        />
      ) : null}

      {actionError || outboxFailed ? (
        <div className="privacy-note error">
          {actionError ||
            'No se pudo guardar la prueba de auditoría. Reintenta; si sigue, faltan credenciales AWS del bucket de auditoría.'}
          {outboxFailed ? (
            <button
              className="btn btn-outline btn-sm"
              disabled={dispatchState.fetching}
              onClick={runDispatch}
              style={{ marginLeft: 12 }}
            >
              {dispatchState.fetching ? 'Reintentando…' : 'Reintentar ahora'}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="privacy-stats">
        {stats.map((stat) => {
          const active = stat.filter && statusFilter === stat.filter
          const clickable = Boolean(stat.filter)
          const Tag = clickable ? 'button' : 'div'
          return (
            <Tag
              key={stat.label}
              type={clickable ? 'button' : undefined}
              title={stat.title}
              className={`privacy-stat ${stat.value > 0 ? stat.tone : ''} ${
                clickable ? 'is-clickable' : ''
              } ${active ? 'is-active' : ''}`}
              onClick={clickable ? () => applyStatFilter(stat) : undefined}
            >
              <span className="privacy-stat-value">{stat.value ?? '—'}</span>
              <span className="privacy-stat-label">{stat.label}</span>
            </Tag>
          )
        })}
      </div>

      <div className="privacy-card">
        <div className="filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">Estado</label>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                const next = e.target.value
                setStatusFilter(next)
                if (next === 'BLOCKS_OPEN' || next === 'ERASURES_OPEN') {
                  setTypeFilter('')
                }
              }}
            >
              <option value="OPEN">Abiertos</option>
              <option value="OVERDUE">Sólo vencidos</option>
              <option value="DUE_SOON">Vencen en ≤7 días</option>
              <option value="AWAITING_ACK">Sin acuse</option>
              <option value="PENDING_CLOSE">Por cerrar</option>
              <option value="HOLDS_UNDECIDED">Bloqueos por decidir</option>
              <option value="MISSING_COMPANY">Sin empresa</option>
              <option value="ERASURES_OPEN">Supresiones abiertas</option>
              <option value="BLOCKS_OPEN">Bloqueos abiertos</option>
              <option value="ALL">Todos</option>
              {Object.entries(CASE_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Derecho</label>
            <select
              className="filter-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {Object.entries(CASE_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group grow">
            <label className="filter-label">Buscar</label>
            <input
              className="search-input"
              placeholder="Código del caso, correo o RUT del titular"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="privacy-note error">{error.message.replace('[GraphQL] ', '')}</div>
      ) : null}

      {fetching && !cases.length ? (
        <div className="loading-spinner">Cargando solicitudes…</div>
      ) : null}

      {!fetching && !cases.length ? (
        <div className="empty-state">
          <p>No hay solicitudes con estos filtros.</p>
        </div>
      ) : null}

      {cases.length ? (
        <table className="data-table privacy-table">
          <thead>
            <tr>
              <th>Caso</th>
              <th>Derecho</th>
              <th>Titular</th>
              <th>Empresa</th>
              <th>Estado</th>
              <th>Vence</th>
              <th>Bloqueo</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cases.map((item) => (
              <tr key={item.id} className={item.isOverdue ? 'privacy-row-overdue' : ''}>
                <td>
                  <strong>{item.caseCode}</strong>
                  <div className="privacy-muted">{formatDate(item.receivedAt)}</div>
                </td>
                <td>
                  {CASE_TYPE_LABELS[item.type] || item.type}
                  {item.request?.detail ? (
                    <div className="privacy-muted">{item.request.detail}</div>
                  ) : null}
                </td>
                <td>
                  {item.subject?.name || item.subject?.email}
                  {!item.ackAt ? <span className="privacy-flag">sin acuse</span> : null}
                </td>
                <td>
                  {item.companyName || '—'}
                </td>
                <td>{caseStatusLabel(item)}</td>
                <td>
                  <DueBadge privacyCase={item} />
                </td>
                <td>
                  <HoldBadge hold={item.hold} />
                </td>
                <td>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => {
                      setActionError('')
                      setSelected(item)
                    }}
                  >
                    Abrir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      </div>

      <CustomModal
        open={Boolean(selected)}
        onClose={() => {
          setSelected(null)
          closePasswordPrompt()
        }}
        title={selected ? `${selected.caseCode} · ${CASE_TYPE_LABELS[selected.type]}` : ''}
        size="large"
        classNameContent="scfo-modal-form-body privacy-modal-content privacy-case-modal"
        hideActions
        content={
          selected ? (
            // Con la key el borrador de cada paso muere con su caso: sin ella el
            // plan o el destinatario escritos en uno aparecen en el siguiente.
            <CaseDetail
              key={selected.caseCode}
              privacyCase={selected}
              onAction={runAction}
              busy={busy}
              actionError={actionError}
              auditDispatching={dispatchState.fetching}
              onFlushAudit={runDispatch}
              onClose={() => {
                setSelected(null)
                closePasswordPrompt()
              }}
            />
          ) : null
        }
      />

      {/* El cuerpo scrollea porque lleva scfo-modal-form-body:
          getCustomModalContentStyle solo devuelve overflow:auto para esa clase. */}
      <CustomModal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        title="Deberes y plazos — Ley 21.719"
        size="small"
        classNameContent="scfo-modal-form-body privacy-modal-content privacy-guide-content"
        hideActions
        content={<DutiesGuide />}
      />

      <CustomModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nueva solicitud de titular"
        size="small"
        classNameContent="scfo-modal-form-body privacy-modal-content"
        confirmLabel="Crear caso"
        confirmDisabled={
          !createForm.email.trim() ||
          !createForm.companyId.trim() ||
          !createForm.requestDetail.trim() ||
          busy
        }
        isLoading={createState.fetching}
        onConfirm={submitCreate}
        content={
          <>
            <CreateCaseForm form={createForm} setForm={setCreateForm} />
            {actionError ? <div className="privacy-note error">{actionError}</div> : null}
          </>
        }
      />

      <CustomModal
        open={Boolean(passwordPrompt)}
        onClose={closePasswordPrompt}
        title={
          passwordPrompt
            ? dpoPasswordPromptCopy(passwordPrompt.action, passwordPrompt.variables).title
            : 'Confirmar acción sensible'
        }
        size="tiny"
        classNameContent="scfo-modal-form-body privacy-modal-content"
        confirmLabel="Confirmar"
        confirmDisabled={!dpoPassword.trim() || passwordBusy || busy}
        isLoading={passwordBusy || busy}
        onConfirm={submitDpoPassword}
        content={
          <form
            className="privacy-dpo-password-form"
            onSubmit={(event) => {
              event.preventDefault()
              void submitDpoPassword()
            }}
          >
            <p className="privacy-muted">
              {passwordPrompt
                ? dpoPasswordPromptCopy(passwordPrompt.action, passwordPrompt.variables).body
                : 'Esta acción es sensible. Escribe tu contraseña del portal para continuar.'}
            </p>
            <label className="privacy-field">
              <span>Contraseña del portal</span>
              <input
                ref={dpoPasswordInputRef}
                type="password"
                name="dpoPassword"
                autoComplete="current-password"
                autoFocus
                value={dpoPassword}
                onChange={(event) => setDpoPassword(event.target.value)}
              />
            </label>
            {actionError ? <div className="privacy-note error">{actionError}</div> : null}
            <button type="submit" className="privacy-dpo-password-submit" tabIndex={-1}>
              Confirmar
            </button>
          </form>
        }
      />
    </div>
  )
}

export default PrivacyList
